import React, { useState, useEffect, useRef } from 'react';
import ApiService from '../services/api';
import { Activity, Child } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import LocalNotificationService, { REMINDER_OPTIONS } from '../services/localNotifications';
import './CalendarScreen.css';
import { formatDateUK } from '../utils/dateFormat';

interface CalendarScreenProps {
  initialDate?: string;
  initialViewMode?: 'month' | 'week';
  onNavigateToActivity?: (child: Child, activity: Activity, calendarState?: { currentMonth: Date, viewMode: 'month' | 'week', isParentActivity?: boolean, parentUuid?: string }) => void;
}

// Helper function to ensure URLs have proper protocol
const ensureProtocol = (url: string): string => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

const CalendarScreen: React.FC<CalendarScreenProps> = ({ initialDate, initialViewMode = 'week', onNavigateToActivity }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  // Auto-select today's date on initial load for weekly view
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [selectedActivities, setSelectedActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(initialDate ? new Date(initialDate) : new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week'>(initialViewMode);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showChildSelectionModal, setShowChildSelectionModal] = useState(false);
  // Classes this parent reps — an audience they can aim an activity at.
  const [repClasses, setRepClasses] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await apiService.getClasses();
        setRepClasses(response.data || []);
      } catch (error) {
        console.error('Failed to load classes:', error);
      }
    })();
  }, []);
  const [children, setChildren] = useState<Child[]>([]);
  const [connectedActivities, setConnectedActivities] = useState<Activity[]>([]);
  const [invitedActivities, setInvitedActivities] = useState<Activity[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Activity[]>([]);
  const [declinedInvitations, setDeclinedInvitations] = useState<Activity[]>([]);
  // Set all activity types to true by default since we removed the filter UI
  const includeConnected = true; // Enable connected activities
  const includeInvited = true;
  const includePending = true;
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [showActivityDetail, setShowActivityDetail] = useState(false);
  const [activityParticipants, setActivityParticipants] = useState<any>(null);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [selectedChildrenFilter, setSelectedChildrenFilter] = useState<string[]>([]); // Empty means show all; can contain child UUIDs or 'parent:UUID' for parents
  // Activity type filters for weekly view
  const [visibleActivityTypes, setVisibleActivityTypes] = useState({
    private: true,
    shared: true,
    pending: true,
    declined: true
  });
  // Kit tracking state: activityUuid -> { itemName: isPacked }
  const [kitTracking, setKitTracking] = useState<Record<string, Record<string, boolean>>>({});
  const kitTrackingLoadedRef = useRef<Set<string>>(new Set());

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Activity[]>([]);

  // Activity configuration modal state (kit items + designated parent)
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [configureActivity, setConfigureActivity] = useState<Activity | null>(null);
  const [configureKitItems, setConfigureKitItems] = useState<string[]>([]);
  const [configureNewKitItem, setConfigureNewKitItem] = useState('');
  const [configureParentUuid, setConfigureParentUuid] = useState<string>('');
  const [configureSaving, setConfigureSaving] = useState(false);
  const [showSeriesPrompt, setShowSeriesPrompt] = useState(false);
  const [configureReminderMinutes, setConfigureReminderMinutes] = useState<number | null>(null);
  const [parents, setParents] = useState<any[]>([]);

  const apiService = ApiService.getInstance();
  const { user } = useAuth();

  const toggleActivityType = (type: 'private' | 'shared' | 'pending' | 'declined') => {
    setVisibleActivityTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Child color mapping - muted colors, assigned by index to guarantee different colors for siblings
  const childColors = [
    '#5b8cb8', // Muted blue
    '#6a9a6a', // Muted green
    '#b87d7d', // Muted red/rose
    '#c4956a', // Muted orange/tan
    '#8a7ab8', // Muted purple
    '#b87aa8', // Muted pink
    '#5a9a9a', // Muted teal
    '#a8a05a'  // Muted olive/yellow
  ];

  // Get child color by index - guarantees siblings always have different colors
  const getChildColor = (childUuid: string) => {
    if (!childUuid) return childColors[0];
    const childIndex = children.findIndex(c => c.uuid === childUuid);
    if (childIndex === -1) return childColors[0];
    return childColors[childIndex % childColors.length];
  };

  // Get parent color by index - offset from children to avoid same colors
  const getParentColor = (parentUuid: string) => {
    if (!parentUuid) return childColors[0];
    const parentIndex = parents.findIndex(p => p.uuid === parentUuid);
    if (parentIndex === -1) return childColors[0];
    // Offset by number of children to get different colors than children
    return childColors[(parentIndex + children.length) % childColors.length];
  };

  // Get color for either child or parent (handles parent:UUID format)
  const getParticipantColor = (key: string) => {
    if (key.startsWith('parent:')) {
      return getParentColor(key.replace('parent:', ''));
    }
    return getChildColor(key);
  };

  // Activity color function - matches ChildActivityScreen exactly with legend colors
  const getActivityColor = (activity: any) => {
    // Priority: Pitchero invitations > regular invitations > cancelled/can't attend > shared/private status

    // 1. Pitchero pending invitations - green
    if (activity.source === 'pitchero' && activity.isPendingInvitation) {
      return {
        background: 'linear-gradient(135deg, #48bb78, #68d391)', // Green for pending
        borderColor: '#48bb78'
      };
    }

    // 2. Pitchero accepted invitations - dark grey (confirmed)
    if (activity.source === 'pitchero' && activity.isAcceptedInvitation) {
      return {
        background: 'linear-gradient(135deg, #2d3748, #4a5568)', // Dark grey for accepted/confirmed
        borderColor: '#2d3748'
      };
    }

    // 3. Regular pending invitations - green
    if (activity.isPendingInvitation) {
      return {
        background: 'linear-gradient(135deg, #48bb78, #68d391)', // Green for pending
        borderColor: '#48bb78'
      };
    }

    // 4. Regular accepted invitations - light blue
    if (activity.isAcceptedInvitation) {
      return {
        background: 'linear-gradient(135deg, #4299e1, #63b3ed)', // Blue for shared/accepted
        borderColor: '#4299e1'
      };
    }

    // 5. Declined invitations OR host can't attend - light grey
    if (activity.isDeclinedInvitation || activity.is_cancelled) {
      return {
        background: 'linear-gradient(135deg, #a0aec0, #cbd5e0)', // Light grey for declined/can't attend
        borderColor: '#a0aec0'
      };
    }

    // 6. For own activities (not invitations), check if shared or private
    if (activity.is_shared) {
      return {
        background: 'linear-gradient(135deg, #4299e1, #63b3ed)', // Light blue for shared (host)
        borderColor: '#4299e1'
      };
    }

    // 7. Private activities (default) - dark grey
    return {
      background: 'linear-gradient(135deg, #2d3748, #4a5568)',
      borderColor: '#2d3748'
    };
  };

  // Load parents for configuration modal
  const loadParents = async () => {
    try {
      const response = await apiService.getParents();
      if (response.success && response.data) {
        setParents(response.data);
      }
    } catch (error) {
      console.error('Failed to load parents:', error);
    }
  };

  useEffect(() => {
    // Load all data in parallel for better mobile performance
    const loadAllData = async () => {
      const promises = [
        loadActivities(),
        loadChildren(),
        loadDeclinedInvitations(),
        loadParents(),
      ];

      if (includeInvited) {
        promises.push(loadInvitedActivities());
      }
      if (includePending) {
        promises.push(loadPendingInvitations());
      }

      // Wait for all to complete in parallel
      await Promise.all(promises);
    };

    loadAllData();
  }, [currentMonth, viewMode, includeInvited, includePending]); // Added viewMode to reload when switching views

  // Separate useEffect for connected activities that depends on children being loaded
  useEffect(() => {
    if (includeConnected && children.length > 0) {
      loadConnectedActivities();
    }
  }, [children, includeConnected, currentMonth]);

  // DISABLED: Load kit tracking - persistence disabled for now
  // Kit checkboxes work client-side only until we optimize the backend
  /*
  useEffect(() => {
    // Skip if still loading main activities
    if (loading) {
      return;
    }

    const allActivities = [
      ...activities,
      ...invitedActivities,
      ...connectedActivities,
      ...pendingInvitations,
      ...declinedInvitations
    ];

    allActivities.forEach(activity => {
      const activityUuid = (activity as any).activity_uuid || activity.uuid;
      const kitNeeded = (activity as any).kit_needed;

      if (activityUuid && kitNeeded && Array.isArray(kitNeeded) && kitNeeded.length > 0) {
        // Don't block - load async
        loadKitTrackingForActivity(activityUuid).catch(err => {
          console.error('Failed to load kit tracking for', activityUuid, err);
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, invitedActivities, connectedActivities, pendingInvitations, declinedInvitations, loading]);
  */

  // Listen for invitation response events from HomeScreen to refresh calendar
  useEffect(() => {
    const handleInvitationResponded = (event: Event) => {
      console.log('📅 CalendarScreen: Invitation responded event received, refreshing...', (event as CustomEvent).detail);
      // Reload all activity data in parallel (don't await - fire and forget)
      const promises = [
        loadActivities(),
        loadInvitedActivities(),
        loadPendingInvitations(),
        loadDeclinedInvitations(),
      ];

      if (includeConnected) {
        promises.push(loadConnectedActivities());
      }

      Promise.all(promises).catch(err => {
        console.error('Error reloading activities:', err);
      });
    };

    window.addEventListener('activityInvitationResponded', handleInvitationResponded);

    return () => {
      window.removeEventListener('activityInvitationResponded', handleInvitationResponded);
    };
  }, [includeConnected]);

  useEffect(() => {

    if (selectedDate) {
      const ownActivities = activities.filter(activity => {
        if (!activity.start_date) return false;
        const activityDate = activity.start_date.split('T')[0];
        const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityDate;
        return activityDate === selectedDate ||
               (activityDate <= selectedDate && activityEndDate >= selectedDate);
      });

      const connectedActivitiesForDay = includeConnected ? connectedActivities.filter(activity => {
        if (!activity.start_date) return false;
        const activityDate = activity.start_date.split('T')[0];
        const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityDate;
        return activityDate === selectedDate ||
               (activityDate <= selectedDate && activityEndDate >= selectedDate);
      }) : [];

      const invitedActivitiesForDay = includeInvited ? invitedActivities.filter(activity => {
        if (!activity.start_date) return false;
        const activityDate = activity.start_date.split('T')[0];
        const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityDate;
        return activityDate === selectedDate ||
               (activityDate <= selectedDate && activityEndDate >= selectedDate);
      }) : [];

      const pendingInvitationsForDay = includePending ? pendingInvitations.filter(activity => {
        if (!activity.start_date) return false;
        const activityDate = activity.start_date.split('T')[0];
        const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityDate;
        return activityDate === selectedDate ||
               (activityDate <= selectedDate && activityEndDate >= selectedDate);
      }) : [];

      // Also include declined/cancelled activities for display (but not counted in calendar)
      // These come from the declinedInvitations state
      const declinedActivitiesForDay = declinedInvitations.filter(activity => {
        if (!activity.start_date) return false;
        const activityDate = activity.start_date.split('T')[0];
        const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityDate;
        const matchesDate = activityDate === selectedDate ||
               (activityDate <= selectedDate && activityEndDate >= selectedDate);
        return matchesDate;
      });

      // Deduplicate - but allow same activity UUID with different child UUIDs (different invitation statuses per child)
      // Key format: For invitations use "activityUuid:childUuid", for own/connected use just "activityUuid"
      const activityMap = new Map<string, any>();

      // Helper to get unique key for activity
      const getActivityKey = (activity: any) => {
        // For invitations, activity_uuid is the actual activity UUID (activity.uuid is invitation UUID)
        // For own/connected activities, uuid is the activity UUID
        const uuid = activity.activity_uuid || activity.uuid;
        const childUuid = (activity as any).invited_child_uuid;
        // For invitations with child UUID, create composite key to keep them separate
        // For own/connected activities without child UUID, use just activity UUID
        return childUuid ? `${uuid}:${childUuid}` : uuid;
      };

      // Helper to check if activity is in list for specific child
      const isInListForChild = (uuid: string, childUuid: string | undefined, list: any[]) => {
        return list.some(a => {
          const aUuid = a.activity_uuid || a.uuid;
          const aChildUuid = (a as any).invited_child_uuid;
          // Match both UUID and child UUID for invitations
          if (childUuid && aChildUuid) {
            return aUuid === uuid && aChildUuid === childUuid;
          }
          // For non-invitation activities, just match UUID
          return aUuid === uuid && !aChildUuid;
        });
      };

      // Check if an activity UUID has invitation entries (invited/pending/declined)
      const hasInvitationEntries = (uuid: string) => {
        return [...declinedActivitiesForDay, ...pendingInvitationsForDay, ...invitedActivitiesForDay].some(
          a => (a.activity_uuid || a.uuid) === uuid
        );
      };

      // Process own activities first (these have no child UUID)
      // Skip own activities that have invitation entries - those will be shown via invitations instead
      ownActivities.forEach(activity => {
        const uuid = activity.activity_uuid || activity.uuid;
        if (!uuid) return;

        // If this activity has invitation entries, skip the own activity - show invitations instead
        const hasInvitations = hasInvitationEntries(uuid);
        if (hasInvitations) {
          return;
        }

        const key = getActivityKey(activity);
        activityMap.set(key, {
          ...activity,
          isPendingInvitation: false,
          isAcceptedInvitation: false,
          isDeclinedInvitation: false,
          isOwnActivity: true,
          isInvitedActivity: false
        });
      });

      // Process connected activities (these have no child UUID)
      connectedActivitiesForDay.forEach(activity => {
        const uuid = activity.activity_uuid || activity.uuid;
        if (!uuid) return;

        const key = getActivityKey(activity);
        if (!activityMap.has(key)) {
          activityMap.set(key, {
            ...activity,
            isPendingInvitation: false,
            isAcceptedInvitation: false,
            isDeclinedInvitation: false,
            isOwnActivity: false,
            isInvitedActivity: false
          });
        }
      });

      // Process invitations - these have child UUIDs so will create separate entries per child
      // Priority: declined > pending > invited (mutually exclusive per child)
      [...declinedActivitiesForDay, ...pendingInvitationsForDay, ...invitedActivitiesForDay].forEach(activity => {
        const uuid = activity.activity_uuid || activity.uuid;
        const childUuid = (activity as any).invited_child_uuid;
        if (!uuid || !childUuid) return; // Skip invitations without child UUID

        const key = getActivityKey(activity);
        if (!activityMap.has(key)) {
          // Determine status for THIS child
          const isDeclined = isInListForChild(uuid, childUuid, declinedActivitiesForDay);
          const isPending = !isDeclined && isInListForChild(uuid, childUuid, pendingInvitationsForDay);
          const isInvited = !isDeclined && !isPending && isInListForChild(uuid, childUuid, invitedActivitiesForDay);

          // Get activity name from various possible fields
          const activityName = activity.name || (activity as any).activity_name || (activity as any).title;

          activityMap.set(key, {
            ...activity,
            name: activityName, // Ensure name is set from activity_name if needed
            isPendingInvitation: isPending,
            isAcceptedInvitation: isInvited,
            isDeclinedInvitation: isDeclined,
            isOwnActivity: false,
            isInvitedActivity: isInvited || isPending
          });
        } else {
          // Merge data (shouldn't happen with composite keys, but just in case)
          const existing = activityMap.get(key);
          const activityName = activity.name || (activity as any).activity_name || (activity as any).title;
          activityMap.set(key, {
            ...existing,
            name: existing.name || activityName,
            description: existing.description || activity.description,
            host_child_name: existing.host_child_name || activity.host_child_name,
            child_name: existing.child_name || activity.child_name
          });
        }
      });

      const deduplicatedActivities = Array.from(activityMap.values());


      setSelectedActivities(deduplicatedActivities);
    }
  }, [selectedDate, activities, connectedActivities, invitedActivities, pendingInvitations, declinedInvitations, includeConnected, includeInvited, includePending]);

  // Search filtering - find activities matching the search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase().trim();

    // Combine all activities
    const allActivities = [
      ...activities,
      ...connectedActivities,
      ...invitedActivities,
      ...pendingInvitations,
      ...declinedInvitations
    ];

    // Deduplicate by uuid
    const seenUuids = new Set<string>();
    const uniqueActivities = allActivities.filter(activity => {
      const uuid = activity.uuid || (activity as any).activity_uuid;
      if (uuid && seenUuids.has(uuid)) return false;
      if (uuid) seenUuids.add(uuid);
      return true;
    });

    // Filter by search query - check name, description, club, location, venue, kit items
    const filtered = uniqueActivities.filter(activity => {
      const name = (activity.name || '').toLowerCase();
      const description = (activity.description || '').toLowerCase();
      // Check both club_name field and nested club.name object
      const clubName = ((activity as any).club_name || '').toLowerCase();
      const clubObjectName = ((activity as any).club?.name || '').toLowerCase();
      const location = (activity.location || '').toLowerCase();
      const venue = ((activity as any).venue || '').toLowerCase();
      const city = ((activity as any).city || '').toLowerCase();
      const activityType = ((activity as any).activity_type || '').toLowerCase();

      // Check kit_needed array for club/item names
      const kitNeeded = (activity as any).kit_needed;
      const kitMatches = Array.isArray(kitNeeded) && kitNeeded.some((item: string) =>
        (item || '').toLowerCase().includes(query)
      );

      return name.includes(query) ||
             description.includes(query) ||
             clubName.includes(query) ||
             clubObjectName.includes(query) ||
             location.includes(query) ||
             venue.includes(query) ||
             city.includes(query) ||
             activityType.includes(query) ||
             kitMatches;
    });

    // Sort by date (upcoming first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.start_date || '').getTime();
      const dateB = new Date(b.start_date || '').getTime();
      return dateA - dateB;
    });

    setSearchResults(filtered);
  }, [searchQuery, activities, connectedActivities, invitedActivities, pendingInvitations, declinedInvitations]);

  const loadActivities = async () => {
    try {
      setLoading(true);

      // For week view, we need to load activities for the entire week (which may span into next month)
      // Calculate the start of the week containing the current month
      const startOfWeek = new Date(currentMonth);
      const dayOfWeek = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek); // Go back to Sunday

      // Calculate end of week (6 days after start)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      // For month view, we need to load the entire calendar grid which includes days from prev/next month
      // Find the first day shown in the calendar (could be from previous month)
      const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const firstDayOfWeek = firstDayOfMonth.getDay();
      const calendarStartDate = new Date(firstDayOfMonth);
      calendarStartDate.setDate(calendarStartDate.getDate() - firstDayOfWeek);

      // For month view, load full 42 days (6 weeks) to match calendar display
      // Calendar shows 6 weeks, so end date should be start date + 41 days
      const calendarEndDate = new Date(calendarStartDate);
      calendarEndDate.setDate(calendarEndDate.getDate() + 41);

      // Use week range if in week view, otherwise use full calendar grid range
      const startDate = (viewMode === 'week' ? startOfWeek : calendarStartDate).toISOString().split('T')[0];
      const endDate = (viewMode === 'week' ? endOfWeek : calendarEndDate).toISOString().split('T')[0];


      const response = await apiService.getCalendarActivities(startDate, endDate);


      // Debug specific activity
      const recInv2007 = response.data?.find((a: any) => a.name === 'rec inv 2007');
      if (recInv2007) {
      }

      // Check for duplicate UUIDs
      const uuids = response.data?.map((a: any) => a.activity_uuid || a.uuid);
      const duplicates = uuids?.filter((uuid: string, index: number) => uuids.indexOf(uuid) !== index);
      if (duplicates && duplicates.length > 0) {
        console.error('🚨 DUPLICATE ACTIVITY UUIDs DETECTED:', duplicates);
      }

      if (response.success && response.data) {
        setActivities(response.data);
      } else {
        alert(`Error: ${response.error || 'Failed to load activities'}`);
      }
    } catch (error) {
      console.error('❌ Load activities error:', error);
      alert('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const loadChildren = async () => {
    try {
      const response = await apiService.getChildren();
      if (response.success && response.data) {
        setChildren(response.data);
      }
    } catch (error) {
      console.error('Load children error:', error);
    }
  };


  const loadConnectedActivities = async () => {
    try {
      // Load connections for each child individually, like the child activity screen does
      const allConnectedActivities: Activity[] = [];
      
      for (const child of children) {
        if (child.uuid) {
          const response = await apiService.getChildConnections(child.uuid);
          if (response.success && response.data) {
            // The response contains connection activities for this child
            allConnectedActivities.push(...response.data);
          }
        }
      }
      
      setConnectedActivities(allConnectedActivities);
    } catch (error) {
      console.error('Load connected activities error:', error);
      setConnectedActivities([]);
    }
  };

  const loadInvitedActivities = async () => {
    try {
      // Use same date logic as loadActivities
      const startOfWeek = new Date(currentMonth);
      const dayOfWeek = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const firstDayOfWeek = firstDayOfMonth.getDay();
      const calendarStartDate = new Date(firstDayOfMonth);
      calendarStartDate.setDate(calendarStartDate.getDate() - firstDayOfWeek);

      // For month view, load full 42 days (6 weeks) to match calendar display
      // Calendar shows 6 weeks, so end date should be start date + 41 days
      const calendarEndDate = new Date(calendarStartDate);
      calendarEndDate.setDate(calendarEndDate.getDate() + 41);

      const startDate = (viewMode === 'week' ? startOfWeek : calendarStartDate).toISOString().split('T')[0];
      const endDate = (viewMode === 'week' ? endOfWeek : calendarEndDate).toISOString().split('T')[0];
      
      const response = await apiService.getInvitedActivities(startDate, endDate);
      if (response.success && response.data) {
        console.log('🔴 CALENDAR loadInvitedActivities - response.data[0]:', response.data[0]);
        console.log('🔴 activity_source in first activity:', response.data[0]?.activity_source);
        setInvitedActivities(response.data);
      } else {
        setInvitedActivities([]);
      }
    } catch (error) {
      console.error('Load invited activities error:', error);
      setInvitedActivities([]);
    }
  };

  const loadPendingInvitations = async () => {
    try {
      // Use same date logic as loadActivities
      const startOfWeek = new Date(currentMonth);
      const dayOfWeek = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const firstDayOfWeek = firstDayOfMonth.getDay();
      const calendarStartDate = new Date(firstDayOfMonth);
      calendarStartDate.setDate(calendarStartDate.getDate() - firstDayOfWeek);

      // For month view, load full 42 days (6 weeks) to match calendar display
      // Calendar shows 6 weeks, so end date should be start date + 41 days
      const calendarEndDate = new Date(calendarStartDate);
      calendarEndDate.setDate(calendarEndDate.getDate() + 41);

      const startDate = (viewMode === 'week' ? startOfWeek : calendarStartDate).toISOString().split('T')[0];
      const endDate = (viewMode === 'week' ? endOfWeek : calendarEndDate).toISOString().split('T')[0];
      
      
      const response = await apiService.getPendingInvitationsForCalendar(startDate, endDate);
      
      if (response.success && response.data) {
        setPendingInvitations(response.data);
      } else {
        setPendingInvitations([]);
      }
    } catch (error) {
      console.error('Load pending invitations error:', error);
      setPendingInvitations([]);
    }
  };

  const loadDeclinedInvitations = async () => {
    try {
      // Use same date logic as loadActivities
      const startOfWeek = new Date(currentMonth);
      const dayOfWeek = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const firstDayOfWeek = firstDayOfMonth.getDay();
      const calendarStartDate = new Date(firstDayOfMonth);
      calendarStartDate.setDate(calendarStartDate.getDate() - firstDayOfWeek);

      // For month view, load full 42 days (6 weeks) to match calendar display
      // Calendar shows 6 weeks, so end date should be start date + 41 days
      const calendarEndDate = new Date(calendarStartDate);
      calendarEndDate.setDate(calendarEndDate.getDate() + 41);

      const startDate = (viewMode === 'week' ? startOfWeek : calendarStartDate).toISOString().split('T')[0];
      const endDate = (viewMode === 'week' ? endOfWeek : calendarEndDate).toISOString().split('T')[0];


      const response = await apiService.getDeclinedInvitations(startDate, endDate);

      if (response.success && response.data) {
        setDeclinedInvitations(response.data);
      } else {
        setDeclinedInvitations([]);
      }
    } catch (error) {
      console.error('Load declined invitations error:', error);
      setDeclinedInvitations([]);
    }
  };


  const generateCalendarDays = () => {
    
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      // Use local date format to match activity dates without timezone conversion
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // Get local children UUIDs for filtering
      const localChildrenUuids = children.map(c => c.uuid);
      
      // Filter activities by participant (child or parent) if filter is applied
      const shouldIncludeActivity = (activity: any) => {
        if (selectedChildrenFilter.length === 0) return true;

        const filterValue = selectedChildrenFilter[0];
        // If filtering by a parent
        if (filterValue?.startsWith('parent:')) {
          const filterParentUuid = filterValue.replace('parent:', '');
          return activity.participant_parent_uuid === filterParentUuid;
        }

        // Otherwise filter by child UUID
        const childUuid = activity.child_uuid || activity.invited_child_uuid;
        return childUuid ? selectedChildrenFilter.includes(childUuid) : true;
      };

      // Legacy helper for child-only checks
      const shouldIncludeChild = (childUuid: string) => {
        if (selectedChildrenFilter.length === 0) return true;
        const filterValue = selectedChildrenFilter[0];
        // If filtering by parent, don't show child activities
        if (filterValue?.startsWith('parent:')) return false;
        return selectedChildrenFilter.includes(childUuid);
      };

      // Own activities: where your children are the hosts OR parent activities
      const ownActivities = activities.filter(activity => {
        if (!activity.start_date) return false; // Skip activities without dates
        const activityDate = activity.start_date.split('T')[0]; // Handle full datetime format
        const matchesDate = activityDate === dateString ||
          (activity.start_date <= dateString && (activity.end_date || activity.start_date) >= dateString);

        // Check if this is a parent activity
        const isParentActivity = !!(activity as any).participant_parent_uuid;
        if (isParentActivity) {
          // Include parent activities based on filter
          return matchesDate && shouldIncludeActivity(activity);
        }

        // Child activity
        const isLocalChild = activity.child_uuid && localChildrenUuids.includes(activity.child_uuid);
        const matchesChild = activity.child_uuid ? shouldIncludeChild(activity.child_uuid) : true;
        return matchesDate && isLocalChild && matchesChild;
      });

      const connectedActivitiesForDay = includeConnected ? connectedActivities.filter(activity => {
        if (!activity.start_date) return false; // Skip activities without dates
        const activityDate = activity.start_date.split('T')[0]; // Handle full datetime format
        const matchesDate = activityDate === dateString || 
          (activity.start_date <= dateString && (activity.end_date || activity.start_date) >= dateString);
        const isLocalChild = activity.child_uuid && localChildrenUuids.includes(activity.child_uuid);
        const matchesChild = activity.child_uuid ? shouldIncludeChild(activity.child_uuid) : true;
        return matchesDate && isLocalChild && matchesChild;
      }) : [];

      // Invited activities: where your children were invited and accepted
      const invitedActivitiesForDay = includeInvited ? invitedActivities.filter(activity => {
        if (!activity.start_date) return false; // Skip activities without dates
        const activityDate = activity.start_date.split('T')[0]; // Handle full datetime format
        const matchesDate = activityDate === dateString || 
          (activity.start_date <= dateString && (activity.end_date || activity.start_date) >= dateString);
        // For invited activities, check if one of your children was invited
        const invitedChildUuid = (activity as any).invited_child_uuid;
        const isLocalChildInvited = invitedChildUuid && localChildrenUuids.includes(invitedChildUuid);
        const matchesChild = invitedChildUuid ? shouldIncludeChild(invitedChildUuid) : true;
        return matchesDate && isLocalChildInvited && matchesChild;
      }) : [];

      // Pending invitations: where your children were invited but haven't responded
      const pendingInvitationsForDay = includePending ? pendingInvitations.filter(activity => {
        if (!activity.start_date) return false; // Skip activities without dates
        const activityDate = activity.start_date.split('T')[0];
        const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityDate;
        const matchesDate = activityDate === dateString ||
               (activityDate <= dateString && activityEndDate >= dateString);
        // For pending invitations, check if one of your children was invited
        const invitedChildUuid = (activity as any).invited_child_uuid;
        const isLocalChildInvited = invitedChildUuid && localChildrenUuids.includes(invitedChildUuid);
        const matchesChild = invitedChildUuid ? shouldIncludeChild(invitedChildUuid) : true;
        return matchesDate && isLocalChildInvited && matchesChild;
      }) : [];

      // Declined invitations: Include declined activities in calendar count
      const declinedInvitationsForDay = declinedInvitations.filter(activity => {
        if (!activity.start_date) return false; // Skip activities without dates
        const activityDate = activity.start_date.split('T')[0];
        const activityEndDate = activity.end_date ? activity.end_date.split('T')[0] : activityDate;
        const matchesDate = activityDate === dateString ||
               (activityDate <= dateString && activityEndDate >= dateString);
        // For declined invitations, check if one of your children was invited
        const invitedChildUuid = (activity as any).invited_child_uuid;
        const isLocalChildInvited = invitedChildUuid && localChildrenUuids.includes(invitedChildUuid);
        const matchesChild = invitedChildUuid ? shouldIncludeChild(invitedChildUuid) : true;
        return matchesDate && isLocalChildInvited && matchesChild;
      });

      // Deduplicate activities - same activity shouldn't be counted multiple times
      // Check which activities have invitation entries (to avoid double-counting own activities with invitations)
      const hasInvitationEntries = (uuid: string) => {
        return [...invitedActivitiesForDay, ...pendingInvitationsForDay, ...declinedInvitationsForDay].some(
          a => (a.activity_uuid || a.uuid) === uuid
        );
      };

      const seenUuids = new Set<string>();
      const dayActivities: any[] = [];

      // Process own activities - skip if they have invitation entries
      ownActivities.forEach(activity => {
        const uuid = activity.uuid || activity.activity_uuid;
        if (uuid && !seenUuids.has(uuid) && !hasInvitationEntries(uuid)) {
          seenUuids.add(uuid);
          dayActivities.push(activity);
        }
      });

      // Process other activities (including declined invitations for calendar count)
      [...connectedActivitiesForDay, ...invitedActivitiesForDay, ...pendingInvitationsForDay, ...declinedInvitationsForDay].forEach(activity => {
        const uuid = activity.activity_uuid || activity.uuid;
        if (uuid && !seenUuids.has(uuid)) {
          seenUuids.add(uuid);
          dayActivities.push(activity);
        }
      });

      // Apply activity type filter (monthly view)
      let filteredDayActivities = dayActivities.filter(activity => {
        if (activity.isPendingInvitation && !visibleActivityTypes.pending) return false;
        if (activity.isDeclinedInvitation && !visibleActivityTypes.declined) return false;
        if (activity.isAcceptedInvitation && !visibleActivityTypes.shared) return false;
        // For own activities, check if shared or private
        if (!activity.isPendingInvitation && !activity.isDeclinedInvitation && !activity.isAcceptedInvitation) {
          if (activity.is_shared && !visibleActivityTypes.shared) return false;
          if (!activity.is_shared && !visibleActivityTypes.private) return false;
        }
        return true;
      });

      // Apply search filter if searching
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        filteredDayActivities = filteredDayActivities.filter(activity => {
          const name = (activity.name || '').toLowerCase();
          const description = (activity.description || '').toLowerCase();
          const clubName = (activity.club_name || '').toLowerCase();
          const location = (activity.location || '').toLowerCase();
          const venue = (activity.venue || '').toLowerCase();
          return name.includes(query) ||
                 description.includes(query) ||
                 clubName.includes(query) ||
                 location.includes(query) ||
                 venue.includes(query);
        });
      }

      // Group activities by child/parent for color coding - separate active and declined counts
      const activitiesByChild = filteredDayActivities.reduce((acc, activity) => {
        // Check if this is a parent activity (has participant_parent_uuid and no child_uuid)
        const participantParentUuid = (activity as any).participant_parent_uuid;
        const isParentActivity = !!participantParentUuid && !activity.child_uuid;

        let participantKey: string;
        if (isParentActivity) {
          // Use parent:UUID format for parent activities
          participantKey = `parent:${participantParentUuid}`;
        } else {
          // For invited activities, use the invited child UUID; for own activities, use the host child UUID
          const isInvited = invitedActivitiesForDay.includes(activity) || pendingInvitationsForDay.includes(activity) || declinedInvitationsForDay.includes(activity);
          participantKey = isInvited
            ? ((activity as any).invited_child_uuid || activity.child_uuid || 'unknown')
            : (activity.child_uuid || 'unknown');
        }

        if (!acc[participantKey]) {
          acc[participantKey] = {
            all: [],
            active: [],
            declined: []
          };
        }
        acc[participantKey].all.push(activity);

        // Separate declined activities for visual distinction
        if (activity.isDeclinedInvitation) {
          acc[participantKey].declined.push(activity);
        } else {
          acc[participantKey].active.push(activity);
        }
        return acc;
      }, {} as Record<string, { all: any[], active: any[], declined: any[] }>);

      const childrenWithActivities = Object.keys(activitiesByChild).filter(key => key !== 'unknown');

      // Debug removed - calendar logic is working correctly


      // Calendar should only show confirmed activities, not connection request notifications
      const hasActivities = filteredDayActivities.length > 0;
      
      // Only show activity-based colors and indicators
      let primaryColor = null;
      
      if (hasActivities) {
        // Use child/parent-specific colors for activities
        if (childrenWithActivities.length === 1) {
          // Single participant - use their color
          const participantKey = childrenWithActivities[0];
          primaryColor = getParticipantColor(participantKey);
        } else if (childrenWithActivities.length > 1) {
          // Multiple participants - use first one's color as primary
          primaryColor = getParticipantColor(childrenWithActivities[0]);
        } else {
          // Fallback color
          primaryColor = '#007bff';
        }
      }
      
      days.push({
        date: new Date(current),
        dateString,
        isCurrentMonth: current.getMonth() === currentMonth.getMonth(),
        isToday: dateString === (() => {
          const today = new Date();
          const todayYear = today.getFullYear();
          const todayMonth = String(today.getMonth() + 1).padStart(2, '0');
          const todayDay = String(today.getDate()).padStart(2, '0');
          return `${todayYear}-${todayMonth}-${todayDay}`;
        })(),
        isSelected: dateString === selectedDate,
        hasActivities,
        activityCount: filteredDayActivities.length,
        activities: filteredDayActivities,
        primaryColor,
        activitiesByChild, // Child-specific activities for rendering multiple children
        childrenWithActivities // Array of child UUIDs with activities this day
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentMonth);
    if (viewMode === 'week') {
      // Navigate week by week
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      // Navigate month by month
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setCurrentMonth(newDate);
  };

  // Generate week days for week view - matches ChildActivityScreen exactly
  const generateWeekDays = () => {
    const startOfWeek = new Date(currentMonth);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day); // Start from Sunday


    const days = [];
    const current = new Date(startOfWeek);

    // Get local children UUIDs for filtering
    const localChildrenUuids = children.map(c => c.uuid);
    // Get local parent UUIDs for filtering parent activities
    const localParentUuids = parents.map(p => p.uuid);

    for (let i = 0; i < 7; i++) {
      const dateString = current.toISOString().split('T')[0];

      // Get all activities for this date - FILTER BY LOCAL CHILDREN OR PARENTS
      const dayActivities = activities.filter(activity => {
        const activityDate = activity.start_date ? activity.start_date.split('T')[0] : '';
        const matchesDate = activityDate === dateString;
        // Filter to only show activities where YOUR children are the hosts
        const isLocalChild = activity.child_uuid && localChildrenUuids.includes(activity.child_uuid);
        // Also include parent activities (no child_uuid, has participant_parent_uuid)
        const participantParentUuid = (activity as any).participant_parent_uuid;
        const isParentActivity = !activity.child_uuid && participantParentUuid && localParentUuids.includes(participantParentUuid);
        return matchesDate && (isLocalChild || isParentActivity);
      });

      const dayConnectedActivities = connectedActivities.filter(activity => {
        const activityDate = activity.start_date ? activity.start_date.split('T')[0] : '';
        const matchesDate = activityDate === dateString;
        // Filter to only show connected activities for YOUR children
        const isLocalChild = activity.child_uuid && localChildrenUuids.includes(activity.child_uuid);
        return matchesDate && isLocalChild;
      });

      const dayInvitedActivities = invitedActivities.filter(activity => {
        const activityDate = activity.start_date ? activity.start_date.split('T')[0] : '';
        const matchesDate = activityDate === dateString;
        // Filter to only show invitations where YOUR children were invited
        const invitedChildUuid = (activity as any).invited_child_uuid;
        const isLocalChildInvited = invitedChildUuid && localChildrenUuids.includes(invitedChildUuid);
        return matchesDate && isLocalChildInvited;
      });

      const dayPendingInvitations = pendingInvitations.filter(activity => {
        const activityDate = activity.start_date ? activity.start_date.split('T')[0] : '';
        const matchesDate = activityDate === dateString;
        // Filter to only show pending invitations where YOUR children were invited
        const invitedChildUuid = (activity as any).invited_child_uuid;
        const isLocalChildInvited = invitedChildUuid && localChildrenUuids.includes(invitedChildUuid);
        return matchesDate && isLocalChildInvited;
      });

      const dayDeclinedInvitations = declinedInvitations.filter(activity => {
        const activityDate = activity.start_date ? activity.start_date.split('T')[0] : '';
        const matchesDate = activityDate === dateString;
        // Filter to only show declined invitations where YOUR children were invited
        const invitedChildUuid = (activity as any).invited_child_uuid;
        const isLocalChildInvited = invitedChildUuid && localChildrenUuids.includes(invitedChildUuid);
        return matchesDate && isLocalChildInvited;
      });

      // Create invitation-only activities (pending, accepted, and declined invitations not in main activities)
      const invitationOnlyActivities = [
        ...dayPendingInvitations.filter(inv => {
          const hasMatchingActivity = dayActivities.some(act => act.uuid === inv.uuid || act.id === inv.id);
          return !hasMatchingActivity;
        }).map(invitation => ({
          ...invitation,
          id: `pending-${invitation.id}`,
          activity_id: invitation.id,
          name: (invitation as any).activity_name || invitation.name,
          description: (invitation as any).activity_description || invitation.description,
          isPendingInvitation: true,
          showEnvelope: true,
          invitationId: invitation.invitation_uuid,
          invitationUuid: invitation.invitation_uuid,
          host_child_name: (invitation as any).host_child_name,
          host_parent_name: (invitation as any).host_parent_username
        })),
        ...dayInvitedActivities.filter(inv => {
          const hasMatchingActivity = dayActivities.some(act => act.uuid === inv.uuid || act.id === inv.id);
          return !hasMatchingActivity;
        }).map(invitation => ({
          ...invitation,
          id: `accepted-${invitation.id}`,
          activity_id: invitation.id,
          name: (invitation as any).activity_name || invitation.name,
          description: (invitation as any).activity_description || invitation.description,
          isAcceptedInvitation: true,
          showAcceptedIcon: !(invitation as any).viewed_at,
          invitationId: invitation.invitation_uuid,
          host_child_name: (invitation as any).host_child_name,
          host_parent_name: (invitation as any).host_parent_username
        })),
        ...dayDeclinedInvitations.filter(inv => {
          const hasMatchingActivity = dayActivities.some(act => act.uuid === inv.uuid || act.id === inv.id);
          return !hasMatchingActivity;
        }).map(invitation => ({
          ...invitation,
          id: `declined-${invitation.id}`,
          activity_id: invitation.id,
          name: (invitation as any).activity_name || invitation.name,
          description: (invitation as any).activity_description || invitation.description,
          isDeclinedInvitation: true,
          invitationId: invitation.invitation_uuid,
          host_child_name: (invitation as any).host_child_name,
          host_parent_name: (invitation as any).host_parent_username
        }))
      ];

      // Deduplicate using per-child composite keys - same logic as monthly day list
      // This allows same activity to show with different statuses for different children
      const activityMap = new Map<string, any>();

      const getActivityKey = (activity: any) => {
        const uuid = activity.activity_uuid || activity.uuid;
        const childUuid = (activity as any).invited_child_uuid;
        // For invitations with child UUID, create composite key to keep them separate per child
        // For own/connected activities without child UUID, use just activity UUID
        return childUuid ? `${uuid}:${childUuid}` : uuid;
      };

      const isInListForChild = (uuid: string, childUuid: string | undefined, list: any[]) => {
        return list.some(a => {
          const aUuid = a.activity_uuid || a.uuid;
          const aChildUuid = (a as any).invited_child_uuid;
          if (childUuid && aChildUuid) {
            return aUuid === uuid && aChildUuid === childUuid;
          }
          return aUuid === uuid && !aChildUuid;
        });
      };

      const hasInvitationEntries = (uuid: string) => {
        return [...dayDeclinedInvitations, ...dayPendingInvitations, ...dayInvitedActivities].some(
          a => (a.activity_uuid || a.uuid) === uuid
        );
      };

      // Process own activities first - skip if they have invitation entries
      dayActivities.forEach(activity => {
        const uuid = activity.activity_uuid || activity.uuid;
        if (!uuid) return;

        if (hasInvitationEntries(uuid)) {
          return; // Skip - will be shown via per-child invitations
        }

        const key = getActivityKey(activity);
        activityMap.set(key, {
          ...activity,
          isPendingInvitation: false,
          isAcceptedInvitation: false,
          isDeclinedInvitation: false
        });
      });

      // Process connected activities
      dayConnectedActivities.forEach(activity => {
        const uuid = activity.activity_uuid || activity.uuid;
        if (!uuid) return;

        const key = getActivityKey(activity);
        if (!activityMap.has(key)) {
          activityMap.set(key, {
            ...activity,
            isPendingInvitation: false,
            isAcceptedInvitation: false,
            isDeclinedInvitation: false
          });
        }
      });

      // Process invitations - these have child UUIDs so will create separate entries per child
      // Priority: declined > pending > invited (mutually exclusive per child)
      [...dayDeclinedInvitations, ...dayPendingInvitations, ...dayInvitedActivities].forEach(activity => {
        const uuid = activity.activity_uuid || activity.uuid;
        const childUuid = (activity as any).invited_child_uuid;
        if (!uuid || !childUuid) return;

        const key = getActivityKey(activity);
        if (!activityMap.has(key)) {
          const isDeclined = isInListForChild(uuid, childUuid, dayDeclinedInvitations);
          const isPending = !isDeclined && isInListForChild(uuid, childUuid, dayPendingInvitations);
          const isInvited = !isDeclined && !isPending && isInListForChild(uuid, childUuid, dayInvitedActivities);


          activityMap.set(key, {
            ...activity,
            name: activity.name || (activity as any).activity_name,
            isPendingInvitation: isPending,
            isAcceptedInvitation: isInvited,
            isDeclinedInvitation: isDeclined
          });
        }
      });

      const allDayItems = Array.from(activityMap.values());

      // Debug logging - show all days with activities
      if (allDayItems.length > 0) {
      }

      // Apply participant filtering if active (can be child UUID or parent:UUID format)
      const filteredActivities = selectedChildrenFilter.length === 0
        ? allDayItems
        : allDayItems.filter(activity => {
            // Check if filtering by a parent
            const filterValue = selectedChildrenFilter[0];
            if (filterValue?.startsWith('parent:')) {
              const filterParentUuid = filterValue.replace('parent:', '');
              // Match activities where this parent is the participant
              const activityParentUuid = (activity as any).participant_parent_uuid;
              return activityParentUuid === filterParentUuid;
            }

            // Otherwise filter by child UUID
            const activityChildUuid = activity.child_uuid || (activity as any).invited_child_uuid;
            return selectedChildrenFilter.includes(activityChildUuid);
          });

      // Sort activities by start time
      const sortedItems = filteredActivities.sort((a, b) => {
        const timeA = a.start_time || '00:00:00';
        const timeB = b.start_time || '00:00:00';
        return timeA.localeCompare(timeB);
      });

      days.push({
        date: new Date(current),
        dateString,
        isToday: dateString === new Date().toISOString().split('T')[0],
        isSelected: dateString === selectedDate,
        hasActivities: sortedItems.length > 0,
        activities: sortedItems,
        activityCount: sortedItems.length,
        hasNotifications: dayPendingInvitations.length > 0
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const handleDateClick = (dateString: string) => {
    setSelectedDate(dateString);

    // Auto-scroll to the activities list section
    setTimeout(() => {
      const activitiesSection = document.querySelector('.activities-list');
      if (activitiesSection) {
        activitiesSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100); // Small delay to ensure the activities have rendered
  };

  const formatTime = (timeString?: string | null) => {
    if (!timeString || typeof timeString !== 'string') return 'All day';
    // Check if it's already "all day" or similar text
    if (timeString.toLowerCase() === 'all day' || !timeString.includes(':')) return timeString;
    // Convert from 24h to 12h format
    const [hours, minutes] = timeString.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatActivityName = (activity: Activity) => {
    const activityName = activity.name || (activity as any).activity_name || '';
    return activityName;
  };

  const formatActivityTime = (activity: Activity) => {
    const activityName = (activity.name || '').toLowerCase();
    const isPickUp = activityName.includes('pick up') || activityName.includes('pickup');

    // For pickup activities, show end_time (the actual pickup time)
    if (isPickUp && activity.end_time) {
      return `Pick Up: ${formatTime(activity.end_time)}`;
    }

    if (activity.start_time && activity.end_time) {
      return `${formatTime(activity.start_time)} - ${formatTime(activity.end_time)}`;
    } else if (activity.start_time) {
      let timeLabel = 'Start';
      if (isPickUp) {
        timeLabel = 'Pick Up';
      } else if (activityName.includes('drop off') || activityName.includes('dropoff') || activityName.includes('drop-off')) {
        timeLabel = 'Drop Off';
      }
      return `${timeLabel}: ${formatTime(activity.start_time)}`;
    }
    return 'All day';
  };

  const loadActivityParticipants = async (activityId: string) => {
    try {
      setLoadingParticipants(true);
      // TODO: Implement batch loading for CalendarScreen to avoid individual API calls
      console.warn('⚠️ CalendarScreen: Individual participants API call disabled for performance - implement batch loading');
      setActivityParticipants(null);
    } catch (error) {
      console.error('Error loading activity participants:', error);
      setActivityParticipants(null);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleActivityClick = (activity: Activity) => {

    // If navigation prop is available, use it to navigate to activity-specific URL
    if (onNavigateToActivity) {
      // Find the child that owns this activity
      const activityWithChild = activity as any;

      // Check if this is a parent activity (no child, has participant_parent_uuid)
      const isParentActivity = !!(activityWithChild.participant_parent_uuid && !activityWithChild.child_uuid);

      if (isParentActivity) {
        // For parent activities, create a pseudo-child object from the parent info
        const parentAsChild = {
          uuid: activityWithChild.participant_parent_uuid,
          name: activityWithChild.participant_parent_name || 'Parent',
          isParent: true
        };
        console.log('🟡 CALENDAR handleActivityClick - parent activity:', activity);
        onNavigateToActivity(parentAsChild as any, activity, {
          currentMonth,
          viewMode,
          isParentActivity: true,
          parentUuid: activityWithChild.participant_parent_uuid
        });
        return;
      }

      // For all invitations (pending/accepted/declined), use invited_child_uuid, otherwise use child_uuid
      const activityUuid = (activity as any).activity_uuid || activity.uuid;
      const isPending = activityWithChild.isPendingInvitation || pendingInvitations.some(a => a.activity_uuid === activityUuid || a.uuid === activityUuid);
      const isDeclined = activityWithChild.isDeclinedInvitation || declinedInvitations.some(a => a.activity_uuid === activityUuid || a.uuid === activityUuid);
      const isAccepted = activityWithChild.isAcceptedInvitation;
      const isAnyInvitation = isPending || isDeclined || isAccepted;

      const childUuid = isAnyInvitation
        ? (activityWithChild.invited_child_uuid || activityWithChild.child_uuid)
        : activityWithChild.child_uuid;


      if (childUuid) {
        // Find the child object with this UUID
        const child = children.find(c => c.uuid === childUuid);
        if (child) {
          console.log('🟡 CALENDAR handleActivityClick - activity passed to nav:', activity);
          console.log('🟡 activity_source in activity:', (activity as any).activity_source);
          onNavigateToActivity(child, activity, { currentMonth, viewMode });
          return;
        }
      }
    }

    // Fallback to modal behavior if navigation not available or child not found
    // Add invitation flags to the activity before setting it as selected
    // For pending invitations, the uuid field IS the invitation UUID (from ai.uuid)
    const isPending = isPendingInvitation(activity);
    const activityWithFlags = {
      ...activity,
      isPendingInvitation: isPending,
      isDeclinedInvitation: isDeclinedInvitation(activity),
      // If it's a pending invitation, uuid is the invitation UUID
      invitationUuid: isPending ? ((activity as any).uuid || (activity as any).invitation_uuid) : ((activity as any).invitation_uuid || (activity as any).invitationUuid),
      invitationId: isPending ? ((activity as any).id || (activity as any).invitation_id) : ((activity as any).invitation_id || (activity as any).invitationId)
    };
    setSelectedActivity(activityWithFlags as any);
    setShowActivityDetail(true);
    // Load participants for the activity (if it's a real activity with a UUID)
    if ((activity as any).activity_uuid && !isPendingInvitation(activity)) {
      loadActivityParticipants((activity as any).activity_uuid);
    } else {
      setActivityParticipants(null);
    }
  };

  const isPendingInvitation = (activity: Activity) => {
    // Pending invitations have both uuid (invitation UUID) and activity_uuid (activity UUID)
    // The activity being displayed has either uuid or activity_uuid as the activity's UUID
    const activityUuid = (activity as any).activity_uuid || activity.uuid;
    return pendingInvitations.some(a =>
      a.activity_uuid === activityUuid ||
      a.uuid === activityUuid ||
      (a as any).id === (activity as any).id
    );
  };

  const isDeclinedInvitation = (activity: Activity) => {
    // For CalendarScreen, we don't currently load declined invitations
    // but we'll add this helper for consistency
    return false;
  };

  const handleInvitationResponse = async (invitationUuid: string, action: 'accept' | 'reject') => {
    try {
      const response = await apiService.respondToActivityInvitation(invitationUuid, action);
      if (response.success) {
        // Close modal first to prevent stale data display
        setShowActivityDetail(false);

        // Reload all activities to reflect the change - await all loads to complete
        await Promise.all([
          loadActivities(),
          includeInvited ? loadInvitedActivities() : Promise.resolve(),
          includePending ? loadPendingInvitations() : Promise.resolve(),
          loadDeclinedInvitations()
        ]);

        const message = action === 'accept'
          ? 'Invitation accepted! Activity will appear in your calendar.'
          : 'Invitation rejected.';
        alert(message);
      } else {
        alert(`Error: ${response.error || 'Failed to respond to invitation'}`);
      }
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
      alert('Failed to respond to invitation');
    }
  };

  const handleKitToggle = async (activityUuid: string, childUuid: string, itemName: string, currentlyPacked: boolean) => {
    // Optimistic update
    const newPackedState = !currentlyPacked;
    const trackingKey = `${activityUuid}-${childUuid}`;

    setKitTracking(prev => ({
      ...prev,
      [trackingKey]: {
        ...prev[trackingKey],
        [itemName]: newPackedState
      }
    }));

    // Persist to backend
    try {
      await apiService.updateKitTracking(activityUuid, childUuid, itemName, newPackedState);

      // Update the activity's kit_tracking field in all activity arrays so it's available when viewing the activity
      const updateActivityKitTracking = (activity: any) => {
        if (activity.uuid === activityUuid || activity.activity_uuid === activityUuid) {
          const updatedKitTracking = { ...(activity.kit_tracking || {}) };
          if (!updatedKitTracking[trackingKey]) {
            updatedKitTracking[trackingKey] = {};
          }
          updatedKitTracking[trackingKey][itemName] = newPackedState;
          return { ...activity, kit_tracking: updatedKitTracking };
        }
        return activity;
      };

      setActivities(prev => prev.map(updateActivityKitTracking));
      setInvitedActivities(prev => prev.map(updateActivityKitTracking));
      setConnectedActivities(prev => prev.map(updateActivityKitTracking));
      setPendingInvitations(prev => prev.map(updateActivityKitTracking));
    } catch (error) {
      console.error('Failed to update kit tracking:', error);
      // Revert on error
      setKitTracking(prev => ({
        ...prev,
        [trackingKey]: {
          ...prev[trackingKey],
          [itemName]: currentlyPacked
        }
      }));
    }
  };

  const loadKitTrackingForActivity = async (activityUuid: string) => {
    // Prevent duplicate loads using ref (doesn't cause re-renders)
    if (kitTrackingLoadedRef.current.has(activityUuid)) {
      return;
    }

    // Mark as loading in ref
    kitTrackingLoadedRef.current.add(activityUuid);

    try {
      const response = await apiService.getKitTracking(activityUuid);
      if (response.success && response.data) {
        const packedItems: Record<string, boolean> = {};
        response.data.forEach((item: any) => {
          packedItems[item.item_name] = item.is_packed;
        });
        setKitTracking(prev => ({
          ...prev,
          [activityUuid]: packedItems
        }));
      }
    } catch (error) {
      console.error('Failed to load kit tracking:', error);
      // Remove from loaded set on error so it can retry
      kitTrackingLoadedRef.current.delete(activityUuid);
    }
  };

  // Open configuration modal for an activity
  const handleOpenConfigureModal = (activity: Activity) => {
    setConfigureActivity(activity);
    setConfigureKitItems((activity as any).kit_needed || []);
    setConfigureParentUuid((activity as any).responsible_parent_uuid || '');
    setConfigureReminderMinutes((activity as any).reminder_minutes ?? null);
    setConfigureNewKitItem('');
    setShowSeriesPrompt(false);
    setShowConfigureModal(true);
  };

  // Save activity configuration
  const handleSaveConfiguration = async (applyToSeries: boolean = false) => {
    if (!configureActivity) return;

    setConfigureSaving(true);
    setShowSeriesPrompt(false);

    try {
      const activityUuid = (configureActivity as any).activity_uuid || configureActivity.uuid;
      const response = await apiService.configureActivity(activityUuid, {
        kit_needed: configureKitItems,
        responsible_parent_uuid: configureParentUuid || null,
        reminder_minutes: configureReminderMinutes,
        apply_to_series: applyToSeries
      });

      if (response.success) {
        // Schedule/cancel local alarm if on native platform
        if (Capacitor.isNativePlatform()) {
          const localNotificationService = LocalNotificationService.getInstance();
          if (configureReminderMinutes !== null) {
            // Request alarm permission (must be in response to user tap for iOS prompt)
            await localNotificationService.requestAlarmPermission();
            await localNotificationService.cancelReminder(activityUuid);
            // For pickup activities, use end_time (the actual pickup time) for reminder scheduling
            const actNameLower = (configureActivity.name || '').toLowerCase();
            const isPickUpActivity = actNameLower.includes('pick up') || actNameLower.includes('pickup');
            const reminderTime = isPickUpActivity && configureActivity.end_time
              ? configureActivity.end_time
              : (configureActivity.start_time || '00:00');
            await localNotificationService.scheduleReminder({
              activityUuid,
              activityName: configureActivity.name,
              startDate: configureActivity.start_date,
              startTime: reminderTime,
              minutesBefore: configureReminderMinutes,
              childName: (configureActivity as any).child_name,
              location: configureActivity.location
            });
          } else {
            await localNotificationService.cancelReminder(activityUuid);
          }
        }

        // Update local state to reflect changes
        const updateActivityConfig = (act: Activity) => {
          const actUuid = (act as any).activity_uuid || act.uuid;
          if (actUuid === activityUuid || (applyToSeries && (act as any).series_id === (configureActivity as any).series_id)) {
            return {
              ...act,
              kit_needed: configureKitItems,
              responsible_parent_uuid: configureParentUuid || null,
              responsible_parent_first_name: parents.find(p => p.uuid === configureParentUuid)?.username || '',
              responsible_parent_last_name: '',
              reminder_minutes: configureReminderMinutes
            } as Activity;
          }
          return act;
        };

        setActivities(prev => prev.map(updateActivityConfig));
        setInvitedActivities(prev => prev.map(updateActivityConfig));
        setConnectedActivities(prev => prev.map(updateActivityConfig));
        setPendingInvitations(prev => prev.map(updateActivityConfig));
        setDeclinedInvitations(prev => prev.map(updateActivityConfig));

        // Update selected activities if any
        setSelectedActivities(prev => prev.map(updateActivityConfig));
        if (selectedActivity) {
          setSelectedActivity(updateActivityConfig(selectedActivity));
        }

        setShowConfigureModal(false);
        setConfigureActivity(null);
      } else {
        alert('Failed to save configuration: ' + (response.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Failed to save activity configuration:', error);
      alert('Failed to save configuration');
    } finally {
      setConfigureSaving(false);
    }
  };

  // Handle save button click - check if series prompt needed
  const handleConfigureSaveClick = () => {
    if (!configureActivity) return;

    // Check if activity is part of a series
    const hasSeries = (configureActivity as any).series_id;

    if (hasSeries) {
      // Show prompt to ask about applying to series
      setShowSeriesPrompt(true);
    } else {
      // Single activity, just save
      handleSaveConfiguration(false);
    }
  };

  // Add kit item
  const handleAddKitItem = () => {
    const item = configureNewKitItem.trim();
    if (item && !configureKitItems.includes(item)) {
      setConfigureKitItems([...configureKitItems, item]);
      setConfigureNewKitItem('');
    }
  };

  // Remove kit item
  const handleRemoveKitItem = (index: number) => {
    setConfigureKitItems(configureKitItems.filter((_, i) => i !== index));
  };

  // Check if activity is a drop-off (morning activity before 10:00)
  const isDropOffActivity = (activity: any): boolean => {
    if (!activity?.start_time) return false;
    const hour = parseInt(activity.start_time.split(':')[0], 10);
    return hour < 10; // Morning activities before 10:00 are considered drop-offs
  };

  // Uniform options matching term planner
  const uniformOptions = ['Smart Uniform', 'PE Kit', 'Home Clothes', 'Costume'];

  const formatDate = (dateString: string) => {
    return formatDateUK(dateString);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Debug: Force data to be ready before generating calendar
  const hasAllData = activities.length > 0 || connectedActivities.length > 0 || invitedActivities.length > 0 || pendingInvitations.length > 0;

  // Don't render week view until we have data and children are loaded
  const isReadyForWeekView = viewMode === 'week' && children.length > 0 && !loading;
  const days = (viewMode === 'month' || !isReadyForWeekView) ? generateCalendarDays() : generateWeekDays();


  return (
    <div className="calendar-screen">
      <div className="calendar-header">
        <div className="calendar-title-row">
          <h2>Calendar</h2>
          <div className="calendar-search-container" style={{ flex: 1, margin: '0 16px', maxWidth: '300px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 32px 8px 12px',
                  borderRadius: '20px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: '#999',
                    border: 'none',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    padding: 0
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div className="filter-controls-group">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
                onClick={() => setViewMode('month')}
              >
                Month
              </button>
              <button
                className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
                onClick={() => setViewMode('week')}
              >
                Week
              </button>
            </div>
            <select
              value={selectedChildrenFilter.length === 0 ? 'all' : selectedChildrenFilter.length === 1 ? selectedChildrenFilter[0] : 'multiple'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'all') {
                  setSelectedChildrenFilter([]);
                } else {
                  setSelectedChildrenFilter([value]);
                }
              }}
              className="single-select"
            >
              <option value="all">Everyone</option>
              {children.filter(c => !c.is_parent).length > 0 && <option disabled>── Children ──</option>}
              {children.filter(c => !c.is_parent).map((child) => (
                <option key={child.uuid} value={child.uuid}>
                  {child.display_name || child.name}
                </option>
              ))}
              {children.filter(c => c.is_parent).length > 0 && <option disabled>── Parents ──</option>}
              {children.filter(c => c.is_parent).map((parentChild) => (
                <option key={parentChild.uuid} value={parentChild.uuid}>
                  {parentChild.display_name || parentChild.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Activity Type Filter - works for both monthly and weekly views */}
        <div className="activity-type-filter">
          <h4>Filter by Activity Type:</h4>
          <div className="filter-grid">
            <div
              className="filter-item"
              onClick={() => toggleActivityType('private')}
              style={{
                cursor: 'pointer',
                opacity: visibleActivityTypes.private ? 1 : 0.4,
                transition: 'opacity 0.2s'
              }}
            >
              <div className="filter-color" style={{ background: 'linear-gradient(135deg, #2d3748, #4a5568)' }}></div>
              <span>{visibleActivityTypes.private ? '✓' : '✗'} Private</span>
            </div>
            <div
              className="filter-item"
              onClick={() => toggleActivityType('shared')}
              style={{
                cursor: 'pointer',
                opacity: visibleActivityTypes.shared ? 1 : 0.4,
                transition: 'opacity 0.2s'
              }}
            >
              <div className="filter-color" style={{ background: 'linear-gradient(135deg, #4299e1, #63b3ed)' }}></div>
              <span>{visibleActivityTypes.shared ? '✓' : '✗'} Shared/Accepted</span>
            </div>
            <div
              className="filter-item"
              onClick={() => toggleActivityType('pending')}
              style={{
                cursor: 'pointer',
                opacity: visibleActivityTypes.pending ? 1 : 0.4,
                transition: 'opacity 0.2s'
              }}
            >
              <div className="filter-color" style={{ background: 'linear-gradient(135deg, #48bb78, #68d391)' }}></div>
              <span>{visibleActivityTypes.pending ? '✓' : '✗'} Pending</span>
            </div>
            <div
              className="filter-item"
              onClick={() => toggleActivityType('declined')}
              style={{
                cursor: 'pointer',
                opacity: visibleActivityTypes.declined ? 1 : 0.4,
                transition: 'opacity 0.2s'
              }}
            >
              <div className="filter-color" style={{ background: 'linear-gradient(135deg, #a0aec0, #cbd5e0)' }}></div>
              <span>{visibleActivityTypes.declined ? '✓' : '✗'} Declined</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add Activity Button - above calendar */}
      <div className="add-activity-section" style={{
        marginTop: '20px',
        marginBottom: '20px',
        padding: '20px',
        borderTop: '2px solid #e2e8f0',
        borderBottom: '2px solid #e2e8f0',
        textAlign: 'center',
        background: '#f7fafc'
      }}>
        <button
          onClick={() => {
            if (!onNavigateToActivity) {
              alert('Navigation not available');
              return;
            }
            if (children.length === 0) {
              alert('Please add children first before creating activities');
              return;
            }
            if (children.length === 1) {
              // Only one child, navigate directly
              onNavigateToActivity(children[0], { uuid: 'new' } as any, {
                currentMonth: currentMonth,
                viewMode: viewMode
              });
            } else {
              // Multiple children, show selection modal
              setShowChildSelectionModal(true);
            }
          }}
          disabled={children.length === 0}
          className="confirm-btn"
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            background: children.length === 0 ? '#cbd5e0' : 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: children.length === 0 ? 'not-allowed' : 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {children.length === 0 ? 'Add children first' : '+ Add Activity'}
        </button>
      </div>

      <div className="calendar-container">
        <div className="calendar-nav">
          <button onClick={() => navigateMonth('prev')} className="nav-btn">
            ‹
          </button>
          <h3 className="month-year">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button onClick={() => navigateMonth('next')} className="nav-btn">
            ›
          </button>
        </div>

        <div className="calendar-grid">
          {/* Only show weekday header in month view - weekly view has its own day headers */}
          {viewMode === 'month' && (
            <div className="weekdays">
              {weekDayNames.map(day => (
                <div key={day} className="weekday">
                  {day}
                </div>
              ))}
            </div>
          )}

          {viewMode === 'week' ? (
            <div className="week-calendar-container">
              {/* Show loading state while data loads */}
              {(loading || children.length === 0) ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '60px 20px',
                  color: '#666',
                  fontSize: '16px'
                }}>
                  <div>Loading calendar...</div>
                </div>
              ) : (
                <>
                  {/* Activity filter moved to header - now applies to both views */}

                  {/* Week view with activity blocks like ChildActivityScreen */}
                  <div className="week-calendar-grid">
              {days.map((day, index) => (
                <div
                  key={index}
                  className={`week-calendar-day ${day.hasActivities ? 'has-activities' : ''} ${(day as any).isSelected ? 'selected' : ''}`}
                  onClick={(e) => {
                    // Allow clicking on empty space to select the day
                    if ((e.target as HTMLElement).closest('.activity-block')) {
                      return; // Don't select day if clicking on activity
                    }
                    handleDateClick(day.dateString);
                  }}
                >
                  <div className="week-day-header">
                    <span className="week-day-number">{day.date.getDate()}</span>
                    <span className="week-day-name">{weekDayNames[day.date.getDay()]}</span>
                  </div>

                  <div className="day-activities">
                    {day.activities.filter((activity: any) => {
                      // Filter activities based on type visibility
                      if (activity.isPendingInvitation && !visibleActivityTypes.pending) return false;
                      if (activity.isDeclinedInvitation && !visibleActivityTypes.declined) return false;
                      if (activity.isAcceptedInvitation && !visibleActivityTypes.shared) return false;
                      // For own activities, check if shared or private
                      if (!activity.isPendingInvitation && !activity.isDeclinedInvitation && !activity.isAcceptedInvitation) {
                        if (activity.is_shared && !visibleActivityTypes.shared) return false;
                        if (!activity.is_shared && !visibleActivityTypes.private) return false;
                      }
                      return true;
                    }).map((activity: any, activityIndex: number) => {
                      const colors = getActivityColor(activity);

                      // Check if this is a parent activity
                      const participantParentUuid = (activity as any).participant_parent_uuid;
                      const isParentActivity = !!participantParentUuid && !activity.child_uuid;

                      let participantName: string;
                      let participantColor: string;
                      let participantKey: string;

                      if (isParentActivity) {
                        // Parent activity - look up parent name
                        const parent = parents.find(p => p.uuid === participantParentUuid);
                        participantName = parent?.username || parent?.email || 'Parent';
                        participantKey = `parent:${participantParentUuid}`;
                        participantColor = getParentColor(participantParentUuid);
                      } else {
                        // Child activity
                        // For invited/pending/declined activities, use invited_child_uuid (YOUR child who was invited)
                        // For own activities, use child_uuid (YOUR child who is the host)
                        const isInvitedActivity = activity.isPendingInvitation || activity.isAcceptedInvitation || activity.isDeclinedInvitation;
                        const childUuid = isInvitedActivity
                          ? (activity.invited_child_uuid || activity.child_uuid)
                          : (activity.child_uuid || activity.invited_child_uuid);
                        const child = children.find(c => c.uuid === childUuid);
                        participantName = child?.name || activity.child_name || 'Unknown';
                        participantKey = childUuid;
                        participantColor = getChildColor(childUuid);
                      }

                      const nameParts = participantName.split(' ');
                      const firstInitial = nameParts[0]?.charAt(0).toUpperCase() || '';
                      const lastInitial = nameParts[1]?.charAt(0).toUpperCase() || '';
                      const participantInitials = firstInitial + lastInitial;

                      return (
                        <div
                          key={`${activity.uuid || activity.id}-${activityIndex}`}
                          className="activity-block"
                          style={{
                            background: colors.background,
                            position: 'relative'
                          }}
                          onClick={() => handleActivityClick(activity)}
                        >
                          <div className="activity-content">
                            <div className="activity-time">
                              {activity.start_time ? formatTime(activity.start_time) : 'All day'}
                            </div>
                            <div className="activity-name">
                              {formatActivityName(activity)}
                            </div>
                            {/* Location removed from weekly view to save space */}
                          </div>

                          {/* Participant initials circle - positioned top-right */}
                          <div className="child-initials-circle" style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            backgroundColor: participantColor,
                            color: 'white',
                            fontSize: '8px',
                            fontWeight: '700',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: isParentActivity ? '2px solid rgba(255,255,255,0.8)' : '1px solid rgba(255,255,255,0.3)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }}>
                            {participantInitials}
                          </div>

                          {/* Notification icons - positioned bottom-right */}
                          {(activity.isPendingInvitation || activity.isAcceptedInvitation || activity.isDeclinedInvitation) && (
                            <div className="activity-footer">
                              {activity.isPendingInvitation && activity.showEnvelope !== false && <span className="notification-icon">📩</span>}
                              {activity.isAcceptedInvitation && activity.showAcceptedIcon && <span className="notification-icon">✅</span>}
                              {activity.isDeclinedInvitation && activity.showDeclinedIcon && <span className="notification-icon">❌</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            // Month view with count indicators
            <div className="days">
              {days.map((day, index) => (
                <div
                  key={index}
                  className={`day ${viewMode === 'month' && !(day as any).isCurrentMonth ? 'other-month' : ''} ${
                    day.isToday ? 'today' : ''
                  } ${(day as any).isSelected ? 'selected' : ''} ${
                    (day as any).hasActivities || day.activityCount > 0 ? 'has-content' : ''
                  }`}
                  onClick={() => handleDateClick(day.dateString)}
                  style={{
                    ...(((day as any).hasActivities || day.activityCount > 0) && (day as any).primaryColor ? {
                      borderLeft: `4px solid ${(day as any).primaryColor}`,
                      backgroundColor: `${(day as any).primaryColor}10`
                    } : {})
                  }}
                >
                  <span className="day-number">{day.date.getDate()}</span>
                  {((day as any).hasActivities || day.activityCount > 0) && (
                    <div className="day-indicators">
                      {((day as any).hasActivities || day.activityCount > 0) && (
                        <div className="activity-indicators">
                          {(day as any).childrenWithActivities?.length > 1 ? (
                            // Multiple children/parents - show stacked indicators (max 2, then +N)
                            <div className="multi-child-indicators">
                              {(() => {
                                const allParticipants = (day as any).childrenWithActivities || [];
                                const maxVisible = 2;
                                const visibleParticipants = allParticipants.slice(0, maxVisible);
                                const remainingCount = allParticipants.length - maxVisible;

                                return (
                                  <>
                                    {visibleParticipants.map((participantKey: string, participantIndex: number) => {
                                      const participantActivities = (day as any).activitiesByChild[participantKey] || { all: [], active: [], declined: [] };
                                      const activeCount = participantActivities.active?.length || 0;
                                      const declinedCount = participantActivities.declined?.length || 0;
                                      const allActivities = participantActivities.all || participantActivities;

                                      // Check if this is a parent or child
                                      const isParent = participantKey.startsWith('parent:');
                                      const actualUuid = isParent ? participantKey.replace('parent:', '') : participantKey;

                                      let participantName: string;
                                      if (isParent) {
                                        const parent = parents.find(p => p.uuid === actualUuid);
                                        participantName = parent?.username || parent?.email || 'Parent';
                                      } else {
                                        participantName = children.find(c => c.uuid === actualUuid)?.name ||
                                                         (Array.isArray(allActivities) ? allActivities[0]?.child_name : allActivities.all?.[0]?.child_name) || 'Unknown';
                                      }

                                      const nameParts = participantName.split(' ');
                                      const firstInitial = nameParts[0]?.charAt(0).toUpperCase() || '';
                                      const lastInitial = nameParts[1]?.charAt(0).toUpperCase() || '';
                                      const participantInitials = firstInitial + lastInitial;

                                      return (
                                        <div
                                          key={participantKey}
                                          className="child-indicator"
                                          style={{
                                            backgroundColor: getParticipantColor(participantKey),
                                            color: 'white',
                                            fontSize: '11px',
                                            padding: '3px 8px',
                                            borderRadius: '6px',
                                            marginBottom: '3px',
                                            fontWeight: 'bold',
                                            minWidth: '45px',
                                            height: '20px',
                                            textAlign: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                          }}
                                          title={`${isParent ? '👔 ' : ''}${participantName}: ${activeCount} active${declinedCount > 0 ? `, ${declinedCount} declined` : ''}`}
                                        >
                                          <span style={{
                                            letterSpacing: '0.5px',
                                            fontSize: '9px',
                                            fontWeight: '700',
                                            backgroundColor: 'rgba(255,255,255,0.3)',
                                            borderRadius: '50%',
                                            width: '16px',
                                            height: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: isParent ? '2px solid rgba(255,255,255,0.8)' : '1px solid rgba(255,255,255,0.4)'
                                          }}>{participantInitials}</span>
                                          <span style={{
                                            fontSize: '13px',
                                            fontWeight: '900',
                                            color: '#fff',
                                            textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                                            display: 'flex',
                                            gap: '3px'
                                          }}>
                                            {activeCount > 0 && <span>{activeCount}</span>}
                                            {declinedCount > 0 && (
                                              <span style={{
                                                color: '#000',
                                                fontSize: '13px'
                                              }}>{declinedCount}</span>
                                            )}
                                          </span>
                                        </div>
                                      );
                                    })}
                                    {remainingCount > 0 && (
                                      <div
                                        className="child-indicator overflow-indicator"
                                        style={{
                                          backgroundColor: '#6b7280',
                                          color: 'white',
                                          fontSize: '12px',
                                          padding: '3px 8px',
                                          borderRadius: '6px',
                                          fontWeight: 'bold',
                                          height: '20px',
                                          textAlign: 'center',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                        }}
                                        title={`${remainingCount} more ${remainingCount === 1 ? 'person' : 'people'} with activities`}
                                      >
                                        +{remainingCount}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            // Single participant (child or parent) - show initials + count
                            (() => {
                              const singleParticipantKey = (day as any).childrenWithActivities?.[0];
                              if (singleParticipantKey) {
                                const participantActivities = (day as any).activitiesByChild[singleParticipantKey] || { all: [], active: [], declined: [] };
                                const activeCount = participantActivities.active?.length || 0;
                                const declinedCount = participantActivities.declined?.length || 0;
                                const allActivities = participantActivities.all || participantActivities;

                                // Check if this is a parent or child
                                const isParent = singleParticipantKey.startsWith('parent:');
                                const actualUuid = isParent ? singleParticipantKey.replace('parent:', '') : singleParticipantKey;

                                let participantName: string;
                                if (isParent) {
                                  const parent = parents.find(p => p.uuid === actualUuid);
                                  participantName = parent?.username || parent?.email || 'Parent';
                                } else {
                                  participantName = children.find(c => c.uuid === actualUuid)?.name ||
                                                   (Array.isArray(allActivities) ? allActivities[0]?.child_name : allActivities.all?.[0]?.child_name) || 'Unknown';
                                }

                                const nameParts = participantName.split(' ');
                                const firstInitial = nameParts[0]?.charAt(0).toUpperCase() || '';
                                const lastInitial = nameParts[1]?.charAt(0).toUpperCase() || '';
                                const participantInitials = firstInitial + lastInitial;

                                return (
                                  <div
                                    className="activity-count"
                                    style={{
                                      backgroundColor: getParticipantColor(singleParticipantKey),
                                      color: 'white',
                                      fontSize: '11px',
                                      padding: '3px 8px',
                                      borderRadius: '6px',
                                      fontWeight: 'bold',
                                      minWidth: '45px',
                                      height: '20px',
                                      textAlign: 'center',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                    }}
                                    title={`${isParent ? '👔 ' : ''}${participantName}: ${activeCount} active${declinedCount > 0 ? `, ${declinedCount} declined` : ''}`}
                                  >
                                    <span style={{
                                      letterSpacing: '0.5px',
                                      fontSize: '9px',
                                      fontWeight: '700',
                                      backgroundColor: 'rgba(255,255,255,0.3)',
                                      borderRadius: '50%',
                                      width: '16px',
                                      height: '16px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: isParent ? '2px solid rgba(255,255,255,0.8)' : '1px solid rgba(255,255,255,0.4)'
                                    }}>{participantInitials}</span>
                                    <span style={{
                                      fontSize: '13px',
                                      fontWeight: '900',
                                      color: '#fff',
                                      textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                                      display: 'flex',
                                      gap: '3px'
                                    }}>
                                      {activeCount > 0 && <span>{activeCount}</span>}
                                      {declinedCount > 0 && (
                                        <span style={{
                                          color: '#000',
                                          fontSize: '13px'
                                        }}>{declinedCount}</span>
                                      )}
                                    </span>
                                  </div>
                                );
                              } else {
                                // Fallback for activities without clear child attribution
                                return (
                                  <div
                                    className="activity-count"
                                    style={{
                                      backgroundColor: '#007bff',
                                      color: 'white',
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      borderRadius: '3px',
                                      fontWeight: 'bold'
                                    }}
                                    title={`${day.activityCount} activit${day.activityCount > 1 ? 'ies' : 'y'}`}
                                  >
                                    {day.activityCount}
                                  </div>
                                );
                              }
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Search Results Section */}
      {searchQuery && (
        <div className="selected-date-section" style={{ borderTop: '3px solid #f59e0b' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#f59e0b' }}>🔍</span>
            Search Results for "{searchQuery}"
            <span style={{
              fontSize: '14px',
              fontWeight: 'normal',
              color: '#666',
              marginLeft: '8px'
            }}>
              ({searchResults.length} {searchResults.length === 1 ? 'activity' : 'activities'} found)
            </span>
          </h3>

          {searchResults.length > 0 ? (
            <div className="activities-list">
              {(() => {
                // Group search results by child/parent - same logic as day list
                const activitiesByChild = searchResults.reduce((groups, activity) => {
                  const isOwnActivity = activities.some(a => a.uuid === activity.uuid || a.id === activity.id);
                  const isInvitedActivity = connectedActivities.some(a => a.uuid === activity.uuid || a.id === activity.id) ||
                                           invitedActivities.some(a => a.uuid === activity.uuid || a.id === activity.id);
                  const isPendingInvitation = (activity as any).isPendingInvitation || pendingInvitations.some(p => p.activity_uuid === activity.uuid);
                  const isDeclinedInvitation = (activity as any).isDeclinedInvitation || declinedInvitations.some(d => d.activity_uuid === activity.uuid);
                  const isAcceptedInvitation = isInvitedActivity && !isPendingInvitation && !isDeclinedInvitation;

                  // Check if this is a parent activity
                  const participantParentUuid = (activity as any).participant_parent_uuid;
                  const isParentActivity = !!participantParentUuid;

                  let childUuid = '';
                  let childName = 'Unknown Child';

                  if (isParentActivity) {
                    childUuid = participantParentUuid;
                    const parentName = (activity as any).participant_parent_name ||
                                      parents.find(p => p.uuid === participantParentUuid)?.username ||
                                      'Parent';
                    childName = `👔 ${parentName}`;
                  } else {
                    childUuid = (isInvitedActivity || isPendingInvitation || isDeclinedInvitation)
                      ? ((activity as any).invited_child_uuid || activity.child_uuid || '')
                      : (activity.child_uuid || '');
                    childName = children.find(c => c.uuid === childUuid)?.name || 'Unknown Child';
                  }

                  if (!groups[childName]) {
                    groups[childName] = {
                      childUuid,
                      activities: []
                    };
                  }
                  groups[childName].activities.push({
                    ...activity,
                    isOwnActivity,
                    isInvitedActivity,
                    isPendingInvitation,
                    isDeclinedInvitation,
                    isAcceptedInvitation,
                    childUuid,
                    childName
                  });
                  return groups;
                }, {} as Record<string, { childUuid: string; activities: any[] }>);

                return Object.entries(activitiesByChild).map(([childName, group]) => {
                  const childColor = getChildColor(group.childUuid);

                  return (
                    <div key={childName} className="child-activity-group" style={{ marginBottom: '20px' }}>
                      {/* Child Name Header */}
                      <div className="child-header" style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        padding: '8px 12px',
                        backgroundColor: childColor,
                        color: 'white',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}>
                        <span>{childName}</span>
                        <span style={{
                          marginLeft: 'auto',
                          fontSize: '12px',
                          opacity: 0.9
                        }}>
                          {group.activities.length} activit{group.activities.length > 1 ? 'ies' : 'y'}
                        </span>
                      </div>

                      {/* Activities for this child */}
                      <div className="child-activities">
                        {group.activities.map((activity, index) => {
                          const activityColors = getActivityColor(activity);
                          const activityDate = activity.start_date ? new Date(activity.start_date) : null;
                          const dateStr = activityDate ? activityDate.toLocaleDateString('en-GB', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: activityDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                          }) : '';

                          let activitySource = '';
                          if (activity.isInvitedActivity) {
                            if (activity.source === 'pitchero') {
                              activitySource = 'Invited by Pitchero';
                            } else {
                              const hostChildName = activity.host_child_name || activity.child_name || 'Friend';
                              const isOwnChildHost = children.some(child => child.name === hostChildName);
                              if (!isOwnChildHost) {
                                activitySource = `Invited by ${hostChildName}`;
                              }
                            }
                          }

                          return (
                            <div
                              key={activity.uuid || index}
                              className="activity-card"
                              style={{
                                background: activityColors.background,
                                color: 'white',
                                marginBottom: '8px',
                                padding: '10px',
                                position: 'relative',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                if (onNavigateToActivity) {
                                  const child = children.find(c => c.uuid === activity.child_uuid);
                                  if (child) {
                                    onNavigateToActivity(child, activity, {
                                      currentMonth,
                                      viewMode,
                                      isParentActivity: !!(activity as any).participant_parent_uuid,
                                      parentUuid: (activity as any).participant_parent_uuid
                                    });
                                  }
                                }
                                setSearchQuery('');
                              }}
                            >
                              {/* Status Badge and View Button */}
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                alignItems: 'flex-end'
                              }}>
                                <div style={{
                                  fontSize: '11px',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontWeight: 'bold',
                                  backgroundColor:
                                    activity.isPendingInvitation ? '#FFA500' :
                                    activity.isDeclinedInvitation ? '#DC3545' :
                                    activity.isAcceptedInvitation ? '#28A745' :
                                    activity.is_shared ? '#007BFF' :
                                    '#6C757D',
                                  color: 'white',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}>
                                  {activity.isPendingInvitation ? 'Pending' :
                                   activity.isDeclinedInvitation ? 'Rejected' :
                                   activity.isAcceptedInvitation ? 'Accepted' :
                                   activity.is_shared ? 'Shared' : 'Private'}
                                </div>
                              </div>

                              {/* Activity Name */}
                              <div style={{
                                fontSize: '15px',
                                fontWeight: 'bold',
                                marginBottom: '4px',
                                color: 'white',
                                paddingRight: '100px'
                              }}>
                                {formatActivityName(activity)}
                              </div>

                              {/* Date and Time */}
                              <div style={{
                                fontSize: '13px',
                                color: 'rgba(255, 255, 255, 0.9)',
                                marginBottom: '4px',
                                fontWeight: '500'
                              }}>
                                📅 {dateStr} {activity.start_time && `• ${formatActivityTime(activity)}`}
                              </div>

                              {/* Website URL */}
                              {activity.website_url && activity.name !== 'School Day' && (
                                <div style={{
                                  marginBottom: '6px',
                                  fontSize: '13px',
                                  color: 'rgba(255, 255, 255, 0.95)'
                                }}>
                                  🌐 <a href={ensureProtocol(activity.website_url)} target="_blank" rel="noopener noreferrer"
                                     onClick={(e) => e.stopPropagation()}
                                     style={{ color: 'rgba(255, 255, 255, 0.95)', textDecoration: 'underline' }}>
                                    {activity.website_url}
                                  </a>
                                </div>
                              )}

                              {/* Responsible Parent */}
                              {(activity as any).responsible_parent_uuid && (
                                <div style={{
                                  color: 'rgba(255, 255, 255, 0.95)',
                                  fontWeight: '600',
                                  marginTop: '6px',
                                  marginBottom: '6px',
                                  fontSize: '13px',
                                  padding: '6px 10px',
                                  background: (activity as any).responsible_parent_uuid === user?.uuid
                                    ? 'rgba(255, 255, 255, 0.25)'
                                    : 'rgba(255, 255, 255, 0.15)',
                                  borderRadius: '6px',
                                  display: 'inline-block',
                                  border: (activity as any).responsible_parent_uuid === user?.uuid
                                    ? '2px solid rgba(255, 255, 255, 0.5)'
                                    : 'none'
                                }}>
                                  🚗 Designated Parent: {`${(activity as any).responsible_parent_first_name || ''} ${(activity as any).responsible_parent_last_name || ''}`.trim() || 'Not specified'}
                                </div>
                              )}

                              {/* Activity Details - Club, Venue, City, Location */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: '12px',
                                fontSize: '12px',
                                color: 'rgba(255, 255, 255, 0.95)'
                              }}>
                                <div>
                                  {((activity as any).club_name || (activity as any).club?.name || activity.venue || activity.city || activity.location) && (
                                    <div style={{
                                      fontSize: '12px',
                                      color: 'rgba(255, 255, 255, 0.95)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '4px'
                                    }}>
                                      {((activity as any).club_name || (activity as any).club?.name) && <span>🏢 {(activity as any).club_name || (activity as any).club?.name}</span>}
                                      {(() => {
                                        const venue = (activity as any).venue || activity.location;
                                        const city = (activity as any).city;
                                        if (venue && city && venue !== city) {
                                          return <span>📍 {venue}, {city}</span>;
                                        } else if (venue || city) {
                                          return <span>📍 {venue || city}</span>;
                                        }
                                        return null;
                                      })()}
                                      {((activity as any).contact_phone || (activity as any).club_contact_phone) && <span>📞 {(activity as any).contact_phone || (activity as any).club_contact_phone}</span>}
                                      {((activity as any).contact_email || (activity as any).club_contact_email) && <span>📧 {(activity as any).contact_email || (activity as any).club_contact_email}</span>}
                                    </div>
                                  )}

                                  {activitySource && (
                                    <div style={{
                                      color: 'rgba(255, 255, 255, 0.95)',
                                      fontWeight: '500',
                                      marginTop: '4px',
                                      fontSize: '12px'
                                    }}>
                                      {activitySource}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Description */}
                              {activity.description && (
                                <div style={{
                                  marginTop: '6px',
                                  fontSize: '12px',
                                  color: 'rgba(255, 255, 255, 0.95)',
                                  fontStyle: 'italic',
                                  lineHeight: '1.4'
                                }}>
                                  📚 {activity.description}
                                </div>
                              )}

                              {/* Kit List */}
                              {activity.kit_needed && Array.isArray(activity.kit_needed) && activity.kit_needed.length > 0 && (
                                <div style={{
                                  marginTop: '8px',
                                  padding: '8px',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: '4px'
                                }}>
                                  <div style={{
                                    fontWeight: 'bold',
                                    marginBottom: '6px',
                                    fontSize: '12px',
                                    color: 'rgba(255, 255, 255, 0.95)'
                                  }}>
                                    🎒 Kit Needed:
                                  </div>
                                  <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '6px'
                                  }}>
                                    {activity.kit_needed.map((item: string, kitIndex: number) => (
                                      <span key={kitIndex} style={{
                                        fontSize: '11px',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        padding: '2px 8px',
                                        borderRadius: '10px'
                                      }}>
                                        {item}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <p className="no-activities" style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
              No activities found matching "{searchQuery}"
            </p>
          )}
        </div>
      )}

      {selectedDate && !searchQuery && (
        <div className="selected-date-section">
          <h3>Activities for {formatDateUK(selectedDate)}</h3>

          {selectedActivities.length > 0 ? (
            <div className="activities-list">
              {(() => {
                // Filter activities based on visibility toggles - SAME AS CALENDAR VIEW
                const filteredActivities = selectedActivities.filter(activity => {
                  // Filter activities based on type visibility
                  if (activity.isPendingInvitation && !visibleActivityTypes.pending) return false;
                  if (activity.isDeclinedInvitation && !visibleActivityTypes.declined) return false;
                  if (activity.isAcceptedInvitation && !visibleActivityTypes.shared) return false;
                  // For own activities, check if shared or private
                  if (!activity.isPendingInvitation && !activity.isDeclinedInvitation && !activity.isAcceptedInvitation) {
                    if (activity.is_shared && !visibleActivityTypes.shared) return false;
                    if (!activity.is_shared && !visibleActivityTypes.private) return false;
                  }
                  return true;
                });

                // Group activities by child/parent - now including pending invitations and parent activities
                const activitiesByChild = filteredActivities.reduce((groups, activity) => {
                  // Check activity type for proper child UUID lookup
                  const isOwnActivity = activities.some(a => a.uuid === activity.uuid || a.id === activity.id);
                  const isInvitedActivity = connectedActivities.some(a => a.uuid === activity.uuid || a.id === activity.id) ||
                                           invitedActivities.some(a => a.uuid === activity.uuid || a.id === activity.id);
                  const isPendingInvitation = activity.isPendingInvitation || false;
                  const isDeclinedInvitation = activity.isDeclinedInvitation || false;

                  // Check if this is a parent activity
                  const participantParentUuid = (activity as any).participant_parent_uuid;
                  const isParentActivity = !!participantParentUuid;

                  // For parent activities, use the parent name
                  let childUuid = '';
                  let childName = 'Unknown Child';

                  if (isParentActivity) {
                    // This is a parent activity - use parent name
                    childUuid = participantParentUuid;
                    const parentName = (activity as any).participant_parent_name ||
                                      parents.find(p => p.uuid === participantParentUuid)?.username ||
                                      'Parent';
                    childName = `👔 ${parentName}`;
                  } else {
                    // For invited/pending/declined activities, try to get the child who was invited
                    childUuid = (isInvitedActivity || isPendingInvitation || isDeclinedInvitation)
                      ? ((activity as any).invited_child_uuid || activity.child_uuid || '')
                      : (activity.child_uuid || '');
                    childName = children.find(c => c.uuid === childUuid)?.name || 'Unknown Child';
                  }
                  
                  if (!groups[childName]) {
                    groups[childName] = {
                      childUuid,
                      activities: []
                    };
                  }
                  groups[childName].activities.push({
                    ...activity,
                    isOwnActivity,
                    isInvitedActivity,
                    childUuid,
                    childName
                  });
                  return groups;
                }, {} as Record<string, { childUuid: string; activities: any[] }>);

                return Object.entries(activitiesByChild).map(([childName, group]) => {
                  const childColor = getChildColor(group.childUuid);

                  return (
                    <div key={childName} className="child-activity-group" style={{ marginBottom: '20px' }}>
                      {/* Child Name Header */}
                      <div className="child-header" style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        padding: '8px 12px',
                        backgroundColor: childColor,
                        color: 'white',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}>
                        <span>{childName}</span>
                        <span style={{
                          marginLeft: 'auto',
                          fontSize: '12px',
                          opacity: 0.9
                        }}>
                          {group.activities.length} activit{group.activities.length > 1 ? 'ies' : 'y'}
                        </span>
                      </div>

                      {/* Activities for this child */}
                      <div className="child-activities">
                        {group.activities.map((activity, index) => {
                          // Use the flags we already determined during grouping
                          const isOwnActivity = activity.isOwnActivity;
                          const isInvitedActivity = activity.isInvitedActivity;

                          // Get activity color based on type
                          const activityColors = getActivityColor(activity);

                          let activitySource = '';
                          if (isInvitedActivity) {
                            // Check if this is a Pitchero activity
                            if (activity.source === 'pitchero') {
                              activitySource = 'Invited by Pitchero';
                            } else {
                              // Show the host child's name for invited activities (connected or accepted)
                              const hostChildName = activity.host_child_name || activity.child_name || 'Friend';
                              // Only show "Invited by" if the host is NOT one of the parent's own children
                              const isOwnChildHost = children.some(child => child.name === hostChildName);
                              if (!isOwnChildHost) {
                                activitySource = `Invited by ${hostChildName}`;
                              }
                            }
                          }

                          return (
                            <div
                              key={index}
                              className="activity-card"
                              style={{
                                background: activityColors.background,
                                color: 'white',
                                marginBottom: '8px',
                                padding: '10px',
                                position: 'relative',
                                borderRadius: '4px'
                              }}
                            >
                              {/* Status Badge and View Button - stacked vertically top-right */}
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                alignItems: 'flex-end'
                              }}>
                                {/* Activity Status Badge */}
                                <div style={{
                                  fontSize: '11px',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontWeight: 'bold',
                                  backgroundColor:
                                    activity.isPendingInvitation ? '#FFA500' : // Orange for pending
                                    activity.isDeclinedInvitation ? '#DC3545' : // Red for rejected
                                    activity.isAcceptedInvitation ? '#28A745' : // Green for accepted
                                    activity.is_shared ? '#007BFF' : // Blue for shared
                                    '#6C757D', // Gray for private
                                  color: 'white',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}>
                                  {activity.isPendingInvitation ? 'Pending' :
                                   activity.isDeclinedInvitation ? 'Rejected' :
                                   activity.isAcceptedInvitation ? 'Accepted' :
                                   activity.is_shared ? 'Shared' : 'Private'}
                                </div>

                                <button
                                className="view-activity-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActivityClick({
                                    ...activity,
                                    uuid: activity.uuid || activity.activity_uuid,
                                    activity_uuid: activity.activity_uuid || activity.uuid,
                                    isPendingInvitation: activity.isPendingInvitation || false,
                                    isDeclinedInvitation: activity.isDeclinedInvitation || false,
                                    isAcceptedInvitation: activity.isAcceptedInvitation || false,
                                    hostParent: activity.source === 'pitchero' ? 'Pitchero' : activity.host_parent_username
                                  });
                                }}
                                style={{
                                  fontSize: '12px',
                                  padding: '6px 12px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                  color: 'white',
                                  border: '1px solid rgba(255, 255, 255, 0.5)',
                                  borderRadius: '16px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                  minWidth: '50px'
                                }}
                              >
                                View
                              </button>
                              </div>

                              {/* Activity Name + Time */}
                              <div className="activity-title-time" style={{
                                fontSize: '15px',
                                fontWeight: 'bold',
                                marginBottom: '4px',
                                color: 'white',
                                paddingRight: '100px' // Make space for status badge and view button
                              }}>
                                {formatActivityName(activity)}
                              </div>

                              {/* Activity Time */}
                              <div className="activity-time-display" style={{
                                fontSize: '13px',
                                color: 'rgba(255, 255, 255, 0.9)',
                                marginBottom: '4px',
                                fontWeight: '500'
                              }}>
                                {formatActivityTime(activity)}
                              </div>

                              {/* Website URL - bigger text, directly below title (but not for School Day which has lessons in description) */}
                              {activity.website_url && activity.name !== 'School Day' && (
                                <div className="activity-url" style={{
                                  marginBottom: '6px',
                                  fontSize: '13px',
                                  color: 'rgba(255, 255, 255, 0.95)'
                                }}>
                                  🌐 <a href={ensureProtocol(activity.website_url)} target="_blank" rel="noopener noreferrer"
                                     style={{ color: 'rgba(255, 255, 255, 0.95)', textDecoration: 'underline' }}>
                                    {activity.website_url}
                                  </a>
                                </div>
                              )}

                              {/* Responsible Parent */}
                              {(activity as any).responsible_parent_uuid && (
                                <div style={{
                                  color: 'rgba(255, 255, 255, 0.95)',
                                  fontWeight: '600',
                                  marginTop: '6px',
                                  marginBottom: '6px',
                                  fontSize: '13px',
                                  padding: '6px 10px',
                                  background: (activity as any).responsible_parent_uuid === user?.uuid
                                    ? 'rgba(255, 255, 255, 0.25)'
                                    : 'rgba(255, 255, 255, 0.15)',
                                  borderRadius: '6px',
                                  display: 'inline-block',
                                  border: (activity as any).responsible_parent_uuid === user?.uuid
                                    ? '2px solid rgba(255, 255, 255, 0.5)'
                                    : 'none'
                                }}>
                                  🚗 Designated Parent: {`${(activity as any).responsible_parent_first_name || ''} ${(activity as any).responsible_parent_last_name || ''}`.trim() || 'Not specified'}
                                </div>
                              )}

                              {/* Activity Details Row */}
                              <div className="activity-details-row" style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: '12px',
                                fontSize: '12px',
                                color: 'rgba(255, 255, 255, 0.95)'
                              }}>
                                <div className="activity-left-details">
                                  {/* Activity Details - Club, Venue, City, Location, Contact Info, Passcode */}
                                  {(activity.club_name || activity.venue || activity.city || activity.location || activity.contact_phone || activity.contact_email || activity.club_contact_phone || activity.club_contact_email || (activity.passcode && activity.is_host)) && (
                                    <div style={{
                                      fontSize: '12px',
                                      color: 'rgba(255, 255, 255, 0.95)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '4px'
                                    }}>
                                      {activity.club_name && <span>🏢 {activity.club_name}</span>}
                                      {(() => {
                                        const venue = activity.venue || activity.location;
                                        const city = activity.city;

                                        if (venue && city && venue !== city) {
                                          return <span>📍 {venue}, {city}</span>;
                                        } else if (venue || city) {
                                          return <span>📍 {venue || city}</span>;
                                        }
                                        return null;
                                      })()}
                                      {(activity.contact_phone || activity.club_contact_phone) && <span>📞 {activity.contact_phone || activity.club_contact_phone}</span>}
                                      {(activity.contact_email || activity.club_contact_email) && <span>📧 {activity.contact_email || activity.club_contact_email}</span>}
                                      {activity.passcode && activity.is_host && <span>🔐 Access Code: {activity.passcode}</span>}
                                    </div>
                                  )}

                                  {/* Invited by - white color */}
                                  {activitySource && (
                                    <div className="activity-source" style={{
                                      color: 'rgba(255, 255, 255, 0.95)',
                                      fontWeight: '500',
                                      marginTop: '4px',
                                      fontSize: '12px'
                                    }}>
                                      {activitySource}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Description if present */}
                              {activity.description && (
                                <div style={{
                                  marginTop: '6px',
                                  fontSize: '12px',
                                  color: 'rgba(255, 255, 255, 0.95)',
                                  fontStyle: 'italic',
                                  lineHeight: '1.4'
                                }}>
                                  📚 {activity.description}
                                </div>
                              )}

                              {/* Kit List - Interactive Checklist */}
                              {(() => {
                                return activity.kit_needed && Array.isArray(activity.kit_needed) && activity.kit_needed.length > 0;
                              })() && (() => {
                                const activityUuid = activity.activity_uuid || activity.uuid;
                                // For invited activities from batch endpoint: use invited_child_uuid
                                // For owned activities from calendar endpoint: use child_uuid
                                const childUuid = activity.invited_child_uuid || activity.child_uuid;
                                // Get tracking from activity.kit_tracking field (from backend)
                                const activityKitTracking = (activity as any).kit_tracking || {};
                                // Key is activityUuid-childUuid
                                const trackingKey = `${activityUuid}-${childUuid}`;
                                const childTracking = activityKitTracking[trackingKey] || {};
                                // Merge with local state for optimistic updates
                                const localTracking = kitTracking[trackingKey] || {};
                                const trackingForActivity = { ...childTracking, ...localTracking };

                                return (
                                  <div style={{
                                    marginTop: '8px',
                                    padding: '8px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '4px'
                                  }}>
                                    <div style={{
                                      fontWeight: 'bold',
                                      marginBottom: '6px',
                                      fontSize: '12px',
                                      color: 'rgba(255, 255, 255, 0.95)'
                                    }}>
                                      🎒 Kit Needed:
                                    </div>
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '4px'
                                    }}>
                                      {activity.kit_needed.map((item: string, kitIndex: number) => {
                                        const isPacked = trackingForActivity[item] || false;
                                        return (
                                          <label key={kitIndex} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '11px',
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            cursor: 'pointer',
                                            padding: '2px 0'
                                          }}>
                                            <input
                                              type="checkbox"
                                              checked={isPacked}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                if (activityUuid && childUuid) {
                                                  handleKitToggle(activityUuid, childUuid, item, isPacked);
                                                }
                                              }}
                                              style={{
                                                cursor: 'pointer',
                                                width: '16px',
                                                height: '16px'
                                              }}
                                            />
                                            <span style={{
                                              textDecoration: isPacked ? 'line-through' : 'none',
                                              opacity: isPacked ? 0.6 : 1
                                            }}>
                                              {item}
                                            </span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Configure button - show when kit OR parent is missing */}
                              {(!activity.kit_needed || activity.kit_needed.length === 0 || !(activity as any).responsible_parent_uuid) && (
                                <div style={{
                                  marginTop: '8px',
                                  padding: '8px',
                                  background: 'rgba(227, 242, 253, 0.95)',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(25, 118, 210, 0.5)'
                                }}>
                                  <div style={{
                                    fontSize: '11px',
                                    color: '#1565c0',
                                    marginBottom: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    flexWrap: 'wrap'
                                  }}>
                                    {!activity.kit_needed || activity.kit_needed.length === 0 ? (isDropOffActivity(activity) ? '👔 No uniform' : '🎒 No kit') : ''}
                                    {(!activity.kit_needed || activity.kit_needed.length === 0) && !(activity as any).responsible_parent_uuid ? ' • ' : ''}
                                    {!(activity as any).responsible_parent_uuid ? '🚗 No parent' : ''}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenConfigureModal(activity);
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#1976d2',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    ⚙️ Add Kit & Parent
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="no-activities">
              <p>No activities scheduled for this day</p>
            </div>
          )}
        </div>
      )}

      {!selectedDate && (
        <div className="instructions">
          <p>Click on a date to view activities for that day</p>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading">Loading activities...</div>
        </div>
      )}

      {/* Enhanced Activity Detail Modal */}
      {showActivityDetail && selectedActivity && (
        <div className="modal-overlay" onClick={() => setShowActivityDetail(false)}>
          <div className="modal activity-detail-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{formatActivityName(selectedActivity)}</h3>
            <div className="activity-detail-content">
              {selectedActivity.isPendingInvitation && (
                <div className="invitation-info">
                  <div className="detail-item">
                    <strong>Invitation from:</strong>
                    {(selectedActivity as any).class_name ? (
                      <p>🎓 {(selectedActivity as any).class_rep_name || 'Your class rep'} (Class {(selectedActivity as any).class_name} rep)</p>
                    ) : (
                      <p>👤 {selectedActivity.hostParent}</p>
                    )}
                  </div>
                  {selectedActivity.message && (
                    <div className="detail-item">
                      <strong>Message:</strong>
                      <p>💬 "{selectedActivity.message}"</p>
                    </div>
                  )}
                  <div className="detail-item">
                    <strong>Status:</strong>
                    <p>
                      <span style={{ color: '#48bb78' }}>📩 Pending</span>
                    </p>
                  </div>
                </div>
              )}
              
              {selectedActivity.description && (
                <div className="detail-item">
                  <strong>Description:</strong>
                  <p>{selectedActivity.description}</p>
                </div>
              )}
              <div className="detail-item">
                <strong>Date & Time:</strong>
                <p>📅 {formatDate(selectedActivity.start_date)} {selectedActivity.start_time && `🕐 ${formatTime(selectedActivity.start_time)}${selectedActivity.end_time ? ` - ${formatTime(selectedActivity.end_time)}` : ''}`}</p>
              </div>
              {selectedActivity.location && (
                <div className="detail-item">
                  <strong>Location:</strong>
                  <p>📍 {selectedActivity.location}</p>
                </div>
              )}
              {(selectedActivity as any).venue && (
                <div className="detail-item">
                  <strong>Venue:</strong>
                  <p>🏛️ {(selectedActivity as any).venue}</p>
                </div>
              )}
              {(selectedActivity as any).city && (
                <div className="detail-item">
                  <strong>City:</strong>
                  <p>🏙️ {(selectedActivity as any).city}</p>
                </div>
              )}
              {(selectedActivity as any).contact_phone && (
                <div className="detail-item">
                  <strong>Contact Phone:</strong>
                  <p>📞 {(selectedActivity as any).contact_phone}</p>
                </div>
              )}
              {(selectedActivity as any).contact_email && (
                <div className="detail-item">
                  <strong>Contact Email:</strong>
                  <p>📧 {(selectedActivity as any).contact_email}</p>
                </div>
              )}
              {(selectedActivity as any).passcode && !(selectedActivity as any).isAcceptedInvitation && !(selectedActivity as any).isPendingInvitation && (
                <div className="detail-item">
                  <strong>Passcode:</strong>
                  <p style={{
                    background: 'rgba(0, 123, 255, 0.1)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    color: '#007bff'
                  }}>🔐 {(selectedActivity as any).passcode}</p>
                </div>
              )}
              {selectedActivity.cost && (
                <div className="detail-item">
                  <strong>Cost:</strong>
                  <p>${selectedActivity.cost}</p>
                </div>
              )}
              {selectedActivity.max_participants && (
                <div className="detail-item">
                  <strong>Max Participants:</strong>
                  <p>{selectedActivity.max_participants}</p>
                </div>
              )}
              {selectedActivity.website_url && (
                <div className="detail-item">
                  <strong>Website:</strong>
                  <p>
                    <a href={ensureProtocol(selectedActivity.website_url)} target="_blank" rel="noopener noreferrer">
                      {selectedActivity.website_url}
                    </a>
                  </p>
                </div>
              )}

              {/* Kit List - Interactive Checklist */}
              {(selectedActivity as any).kit_needed && Array.isArray((selectedActivity as any).kit_needed) && (selectedActivity as any).kit_needed.length > 0 && (() => {
                const activityUuid = (selectedActivity as any).activity_uuid || selectedActivity.uuid;
                // For invited activities: use invited_child_uuid, otherwise use child_uuid
                const childUuid = (selectedActivity as any).invited_child_uuid || (selectedActivity as any).child_uuid;
                const kitNeeded = (selectedActivity as any).kit_needed;
                const trackingKey = `${activityUuid}-${childUuid}`;
                const trackingForActivity = kitTracking[trackingKey] || {};

                return (
                  <div className="detail-item">
                    <strong>🎒 Kit Needed:</strong>
                    <div style={{
                      marginTop: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      {kitNeeded.map((item: string, kitIndex: number) => {
                        const isPacked = trackingForActivity[item] || false;
                        return (
                          <label key={kitIndex} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            padding: '6px 8px',
                            backgroundColor: isPacked ? '#f0f0f0' : 'transparent',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                          }}>
                            <input
                              type="checkbox"
                              checked={isPacked}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (activityUuid && childUuid) {
                                  handleKitToggle(activityUuid, childUuid, item, isPacked);
                                }
                              }}
                              style={{
                                cursor: 'pointer',
                                width: '18px',
                                height: '18px'
                              }}
                            />
                            <span style={{
                              textDecoration: isPacked ? 'line-through' : 'none',
                              opacity: isPacked ? 0.6 : 1,
                              color: isPacked ? '#666' : '#333'
                            }}>
                              {item}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Activity Participants Section */}
              {!selectedActivity.isPendingInvitation && (
                <div className="participants-section">
                  <div className="detail-item">
                    <strong>Participants:</strong>
                    {loadingParticipants ? (
                      <p>Loading participants...</p>
                    ) : activityParticipants ? (
                      <div className="participants-list">
                        <div className="host-info">
                          <p>👤 <strong>{activityParticipants.host?.host_name} (Host)</strong></p>
                        </div>
                        {activityParticipants.participants?.length > 0 ? (
                          <div className="invited-participants">
                            <h4>Invited Families:</h4>
                            {activityParticipants.participants.map((participant: any, index: number) => (
                              <div key={index} className="participant-item">
                                <div className="participant-info">
                                  <span className="participant-name">👤 {participant.parent_name}</span>
                                  {participant.child_name && (
                                    <span className="participant-child"> ({participant.child_name})</span>
                                  )}
                                </div>
                                <div className="participant-status">
                                  {participant.status === 'pending' && (
                                    <span style={{ color: '#fd7e14', fontSize: '12px' }}>📩 Pending</span>
                                  )}
                                  {participant.status === 'accepted' && (
                                    <span style={{ color: '#48bb78', fontSize: '12px' }}>✅ Accepted</span>
                                  )}
                                  {participant.status === 'rejected' && (
                                    <span style={{ color: '#a0aec0', fontSize: '12px' }}>❌ Rejected</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                            No invitations sent yet
                          </p>
                        )}
                      </div>
                    ) : (
                      <p style={{ fontSize: '14px', color: '#666' }}>
                        Unable to load participants
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowActivityDetail(false)}
                className="cancel-btn"
              >
                Close
              </button>
              
              {/* Show accept/decline buttons for pending invitations */}
              {selectedActivity.isPendingInvitation && selectedActivity.invitationId && (
                <>
                  <button
                    onClick={() => handleInvitationResponse(selectedActivity.invitationUuid || String(selectedActivity.invitationId!), 'accept')}
                    className="confirm-btn"
                    style={{ background: 'linear-gradient(135deg, #48bb78, #68d391)', marginRight: '8px' }}
                  >
                    ✅ Accept Invitation
                  </button>
                  <button
                    onClick={() => handleInvitationResponse(selectedActivity.invitationUuid || String(selectedActivity.invitationId!), 'reject')}
                    className="delete-btn"
                  >
                    ❌ Decline Invitation
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Child/Parent Selection Modal for Add Activity */}
      {showChildSelectionModal && (
        <div className="modal-overlay" onClick={() => setShowChildSelectionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h3>Who is this activity for?</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>Select who will be the participant for this activity</p>

            {/* Children Section */}
            {children.filter(c => !c.is_parent).length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Children:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {children.filter(c => !c.is_parent).map((child) => (
                  <button
                    key={child.uuid}
                    onClick={() => {
                      if (onNavigateToActivity) {
                        onNavigateToActivity(child, { uuid: 'new' } as any, {
                          currentMonth: currentMonth,
                          viewMode: viewMode
                        });
                      }
                      setShowChildSelectionModal(false);
                    }}
                    style={{
                      padding: '12px 20px',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    className="confirm-btn"
                  >
                    👤 {child.display_name || child.name}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Classes this parent reps. Picking one opens the activity form
                for the class's own child, with the class already chosen. */}
            {repClasses.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Classes you rep:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {repClasses.map((group: any) => {
                    const ownerChild = children.find(c => c.uuid === (group.my_child_uuid || group.owner_child_uuid));
                    // The rep's own child is in the class too.
                    const total = (group.members || []).length + (group.reps || []).length;
                    const waiting = (group.members || []).filter((m: any) => m.status !== 'accepted').length;
                    if (!ownerChild) return null;
                    return (
                      <button
                        key={group.uuid}
                        onClick={() => {
                          if (onNavigateToActivity) {
                            onNavigateToActivity(ownerChild, { uuid: 'new' } as any, {
                              currentMonth: currentMonth,
                              viewMode: viewMode,
                              preselectClassUuid: group.uuid
                            } as any);
                          }
                          setShowChildSelectionModal(false);
                        }}
                        style={{
                          padding: '12px 20px',
                          fontSize: '16px',
                          fontWeight: '500',
                          background: 'linear-gradient(135deg, #8B3FD9, #6d2fb0)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        🎓 {group.name}
                        <span style={{ opacity: 0.85, fontWeight: 400 }}>
                          {' '}— {total} child{total === 1 ? '' : 'ren'}
                          {waiting ? ` · ${waiting} not yet accepted` : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Parents Section - using parent-child records */}
            {children.filter(c => c.is_parent).length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>Parents:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {children.filter(c => c.is_parent).map((parentChild: any) => (
                    <button
                      key={parentChild.uuid}
                      onClick={() => {
                        if (onNavigateToActivity) {
                          // Parent-child records are treated as regular children for activity creation
                          onNavigateToActivity(parentChild as any, { uuid: 'new' } as any, {
                            currentMonth: currentMonth,
                            viewMode: viewMode
                          });
                        }
                        setShowChildSelectionModal(false);
                      }}
                      style={{
                        padding: '12px 20px',
                        fontSize: '16px',
                        fontWeight: '500',
                        background: 'linear-gradient(135deg, #9575cd, #7e57c2)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      className="confirm-btn"
                    >
                      👔 {parentChild.display_name || parentChild.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => setShowChildSelectionModal(false)}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                background: '#e2e8f0',
                color: '#2d3748',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                width: '100%'
              }}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Activity Configuration Modal (Kit + Parent) */}
      {showConfigureModal && configureActivity && (
        <div className="modal-overlay" onClick={() => !configureSaving && setShowConfigureModal(false)}>
          <div className="modal" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <h3>⚙️ Configure Activity</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              {configureActivity.name}
            </p>

            {/* Series Prompt */}
            {showSeriesPrompt ? (
              <div>
                <p style={{ fontSize: '14px', marginBottom: '16px', color: '#333' }}>
                  This activity is part of a recurring series. Would you like to apply these changes to all sessions in the series, or just this one?
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="cancel-btn"
                    onClick={() => setShowSeriesPrompt(false)}
                    disabled={configureSaving}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveConfiguration(false)}
                    disabled={configureSaving}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: configureSaving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {configureSaving ? 'Saving...' : 'Just This Session'}
                  </button>
                  <button
                    className="confirm-btn"
                    onClick={() => handleSaveConfiguration(true)}
                    disabled={configureSaving}
                    style={{ flex: 1 }}
                  >
                    {configureSaving ? 'Saving...' : 'All Sessions'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Uniform Quick-Add Section (for drop-off activities) */}
                {isDropOffActivity(configureActivity) && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                      👔 Uniform & Essentials
                    </label>
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      Quick add common items for drop-off:
                    </p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {uniformOptions.map((item) => (
                        <button
                          key={item}
                          onClick={() => {
                            if (!configureKitItems.includes(item)) {
                              setConfigureKitItems([...configureKitItems, item]);
                            }
                          }}
                          disabled={configureKitItems.includes(item)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: configureKitItems.includes(item) ? '#d4edda' : '#f8f9fa',
                            color: configureKitItems.includes(item) ? '#155724' : '#333',
                            border: `1px solid ${configureKitItems.includes(item) ? '#28a745' : '#ddd'}`,
                            borderRadius: '16px',
                            cursor: configureKitItems.includes(item) ? 'default' : 'pointer',
                            fontSize: '12px',
                            opacity: configureKitItems.includes(item) ? 0.7 : 1
                          }}
                        >
                          {configureKitItems.includes(item) ? '✓ ' : '+ '}{item}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Kit Items Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                    🎒 {isDropOffActivity(configureActivity) ? 'Additional Items' : 'Kit Items'}
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {configureKitItems.map((item, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: '#e3f2fd',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {item}
                        <button
                          onClick={() => handleRemoveKitItem(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '14px',
                            color: '#666',
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="text"
                      value={configureNewKitItem}
                      onChange={(e) => setConfigureNewKitItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddKitItem()}
                      placeholder="Add kit item..."
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={handleAddKitItem}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Designated Parent Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                    🚗 Designated Parent
                  </label>
                  <select
                    value={configureParentUuid}
                    onChange={(e) => setConfigureParentUuid(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Not assigned</option>
                    {parents.map((parent) => (
                      <option key={parent.uuid} value={parent.uuid}>
                        {parent.username} {parent.uuid === user?.uuid ? '(You)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reminder Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                    Reminder
                  </label>
                  <select
                    value={configureReminderMinutes ?? ''}
                    onChange={(e) => setConfigureReminderMinutes(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">No reminder</option>
                    {REMINDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="modal-actions" style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="cancel-btn"
                    onClick={() => setShowConfigureModal(false)}
                    disabled={configureSaving}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    className="confirm-btn"
                    onClick={handleConfigureSaveClick}
                    disabled={configureSaving}
                    style={{ flex: 1 }}
                  >
                    {configureSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarScreen;